// Illustration SVG procédurale du lieu : biome + type de lieu + moment de la journée (heure de Paris) + météo (déterministe par lieu+heure).
// Couches : ciel (dégradé aube/jour/crépuscule/nuit, étoiles, lune), relief lointain, silhouettes (arbres, maisons, tours, ruines…),
// premier plan, particules animées en CSS (lucioles la nuit, braises, neige, pluie, brume). Hauteur ~200px, largeur 100 %.
// Tout le hasard vient d'un mulberry32 local amorcé par la graine du lieu : même lieu, même heure → même image.
import { useMemo } from "preact/hooks";
import type { JSX } from "preact";
import type { Biome, LocationKind } from "../../shared/types";
import "./Scene.css";

export interface SceneProps { biome: Biome; kind: LocationKind; name: string; seed: string; now: number; danger: number; inCombat?: boolean; height?: number }

export type ScenePhase = "aube" | "jour" | "crepuscule" | "nuit";
export type SceneWeather = "clair" | "brume" | "pluie" | "neige" | "orage";

type El = JSX.Element;
type Pt = [number, number];
type SkyStops = [string, string, string, string];

const HOUR_MS = 3_600_000;

// ───────── Hasard local ─────────

/** Hachage 32 bits (FNV-1a + brassage final) : identique sur tous les appareils. */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

/** mulberry32 : jamais de Math.random, le décor doit rester stable d'un rendu à l'autre. */
class Prng {
  private s: number;
  constructor(seed: number) {
    this.s = seed >>> 0;
  }
  next(): number {
    this.s = (this.s + 0x6d2b79f5) >>> 0;
    let t = this.s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  range(a: number, b: number): number {
    return a + (b - a) * this.next();
  }
  int(a: number, b: number): number {
    return a + Math.floor(this.next() * (b - a + 1));
  }
  pick<T>(items: readonly T[]): T {
    return items[Math.floor(this.next() * items.length)];
  }
  chance(p: number): boolean {
    return this.next() < p;
  }
}

const rngFor = (...parts: (string | number)[]): Prng => new Prng(hashString(parts.join("§")));

// ───────── Heure de Paris, météo, lune ─────────

export interface ParisClock { year: number; month: number; day: number; hour: number; minute: number }

let parisFmt: Intl.DateTimeFormat | null | undefined;

export function parisClock(now: number): ParisClock {
  if (parisFmt === undefined) {
    try {
      parisFmt = new Intl.DateTimeFormat("fr-FR", {
        timeZone: "Europe/Paris", year: "numeric", month: "numeric", day: "numeric",
        hour: "numeric", minute: "numeric", hourCycle: "h23",
      });
    } catch {
      parisFmt = null;
    }
  }
  if (parisFmt) {
    try {
      const parts = parisFmt.formatToParts(new Date(now));
      const get = (t: string): number => parseInt(parts.find((x) => x.type === t)?.value ?? "", 10);
      const c: ParisClock = { year: get("year"), month: get("month"), day: get("day"), hour: get("hour") % 24, minute: get("minute") };
      if ([c.year, c.month, c.day, c.hour, c.minute].every(Number.isFinite)) return c;
    } catch {
      // repli ci-dessous
    }
  }
  const d = new Date(now + HOUR_MS); // repli grossier : heure d'hiver de Paris
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate(), hour: d.getUTCHours(), minute: d.getUTCMinutes() };
}

/** Mêmes bornes que dayPhase() du moteur : aube 6–8 h, jour 8–19 h, crépuscule 19–22 h, nuit sinon. */
export function phaseAt(hour: number): ScenePhase {
  if (hour >= 6 && hour < 8) return "aube";
  if (hour >= 8 && hour < 19) return "jour";
  if (hour >= 19 && hour < 22) return "crepuscule";
  return "nuit";
}

const WEATHER_ORDER: SceneWeather[] = ["clair", "brume", "pluie", "neige", "orage"];
const WEATHER_ODDS: Record<Biome, Partial<Record<SceneWeather, number>>> = {
  plaine: { clair: 56, brume: 16, pluie: 24, orage: 4 },
  foret: { clair: 48, brume: 24, pluie: 24, orage: 4 },
  montagne: { clair: 44, brume: 20, pluie: 10, neige: 23, orage: 3 },
  marais: { clair: 30, brume: 46, pluie: 20, orage: 4 },
  desert: { clair: 86, brume: 7, pluie: 5, orage: 2 },
  cote: { clair: 46, brume: 22, pluie: 26, orage: 6 },
  toundra: { clair: 44, brume: 14, neige: 42 },
  souterrain: { clair: 78, brume: 22 },
  volcan: { clair: 68, brume: 22, pluie: 8, orage: 2 },
  jungle: { clair: 36, brume: 24, pluie: 34, orage: 6 },
};

function slotWeather(seed: string, biome: Biome, c: ParisClock): SceneWeather {
  const r = rngFor("meteo", seed, c.year, c.month, c.day, Math.floor(c.hour / 3));
  const odds = WEATHER_ODDS[biome];
  let total = 0;
  for (const w of WEATHER_ORDER) total += odds[w] ?? 0;
  let roll = r.next() * total;
  for (const w of WEATHER_ORDER) {
    roll -= odds[w] ?? 0;
    if (roll < 0) return w;
  }
  return "clair";
}

/** Météo déterministe : graine du lieu + jour + tranche de 3 h, avec un peu de persistance d'une tranche à l'autre. */
export function sceneWeather(seed: string, biome: Biome, now: number): SceneWeather {
  const c = parisClock(now);
  const keep = rngFor("persistance", seed, c.year, c.month, c.day, Math.floor(c.hour / 3)).chance(0.4);
  return keep ? slotWeather(seed, biome, parisClock(now - 3 * HOUR_MS)) : slotWeather(seed, biome, c);
}

const SYNODIC_DAYS = 29.530588853;
const REF_NEW_MOON = Date.UTC(2000, 0, 6, 18, 14);

/** Âge de la lune pour le jour de Paris : 0 = nouvelle lune, 0,5 = pleine lune. */
export function moonPhase(now: number): number {
  const c = parisClock(now);
  const noon = Date.UTC(c.year, c.month - 1, c.day, 12);
  const cycles = (noon - REF_NEW_MOON) / 86_400_000 / SYNODIC_DAYS;
  return ((cycles % 1) + 1) % 1;
}

export interface SceneAmbience { phase: ScenePhase; weather: SceneWeather; hour: number; moon: number; dayKey: string }

export function sceneAmbience(seed: string, biome: Biome, now: number): SceneAmbience {
  const c = parisClock(now);
  const hour = c.hour + c.minute / 60;
  return { phase: phaseAt(hour), weather: sceneWeather(seed, biome, now), hour, moon: moonPhase(now), dayKey: `${c.year}-${c.month}-${c.day}` };
}

// ───────── Libellés (accessibilité) ─────────

const KIND_LABEL: Record<LocationKind, string> = {
  village: "village", ville: "ville", hameau: "hameau", foret: "forêt", ruines: "ruines", donjon: "donjon",
  grotte: "grotte", lac: "lac", col: "col", sanctuaire: "sanctuaire", tour: "tour", camp: "camp", port: "port",
  tombeau: "tombeau", chateau: "château", clairiere: "clairière",
};
const PHASE_LABEL: Record<ScenePhase, string> = { aube: "aube", jour: "jour", crepuscule: "crépuscule", nuit: "nuit" };
const WEATHER_LABEL: Record<SceneWeather, string> = { clair: "ciel dégagé", brume: "brume", pluie: "pluie", neige: "neige", orage: "orage" };

/** « Brumevault, village, nuit, brume » (+ « combat en cours »). */
export function sceneLabel(name: string, kind: LocationKind, biome: Biome, phase: ScenePhase, weather: SceneWeather, inCombat = false): string {
  const parts = [name, KIND_LABEL[kind], PHASE_LABEL[phase]];
  if (!(biome === "souterrain" && weather === "clair")) parts.push(WEATHER_LABEL[weather]);
  if (inCombat) parts.push("combat en cours");
  return parts.filter(Boolean).join(", ");
}

// ───────── Couleurs ─────────

type Rgb = [number, number, number];
const rgbCache = new Map<string, Rgb>();

function toRgb(hex: string): Rgb {
  const hit = rgbCache.get(hex);
  if (hit) return hit;
  const n = parseInt(hex.slice(1, 7), 16);
  const c: Rgb = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  rgbCache.set(hex, c);
  return c;
}

function toHex(c: Rgb): string {
  const h = (v: number): string => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
  return `#${h(c[0])}${h(c[1])}${h(c[2])}`;
}

function mix(a: string, b: string, t: number): string {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  const x = toRgb(a);
  const y = toRgb(b);
  return toHex([x[0] + (y[0] - x[0]) * k, x[1] + (y[1] - x[1]) * k, x[2] + (y[2] - x[2]) * k]);
}

function desaturate(hex: string, t: number): string {
  const [r, g, b] = toRgb(hex);
  const l = 0.3 * r + 0.59 * g + 0.11 * b;
  return mix(hex, toHex([l, l, l]), t);
}

// ───────── Géométrie ─────────

const f1 = (n: number): string => String(Math.round(n * 10) / 10);
const f2 = (n: number): string => String(Math.round(n * 100) / 100);
const pt = (x: number, y: number): string => `${f1(x)} ${f1(y)}`;
const px = (n: number): string => `${f1(n)}px`;
const q4 = (n: number): number => Math.round(n * 4) / 4;

function polyD(pts: Pt[], close = true): string {
  let d = `M${pt(pts[0][0], pts[0][1])}`;
  for (let i = 1; i < pts.length; i++) d += `L${pt(pts[i][0], pts[i][1])}`;
  return close ? `${d}Z` : d;
}

/** Courbe lisse passant par les milieux des segments. */
function smoothD(pts: Pt[]): string {
  let d = `M${pt(pts[0][0], pts[0][1])}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const [x, y] = pts[i];
    const [nx, ny] = pts[i + 1];
    d += `Q${pt(x, y)} ${pt((x + nx) / 2, (y + ny) / 2)}`;
  }
  const last = pts[pts.length - 1];
  return `${d}L${pt(last[0], last[1])}`;
}

const closeDown = (top: string, bottom = 215): string => `${top}L430 ${bottom}L-30 ${bottom}Z`;

function circleD(cx: number, cy: number, r: number): string {
  return `M${pt(cx - r, cy)}a${f1(r)} ${f1(r)} 0 1 0 ${f1(2 * r)} 0a${f1(r)} ${f1(r)} 0 1 0 ${f1(-2 * r)} 0Z`;
}

function ellipseD(cx: number, cy: number, rx: number, ry: number): string {
  return `M${pt(cx - rx, cy)}a${f1(rx)} ${f1(ry)} 0 1 0 ${f1(2 * rx)} 0a${f1(rx)} ${f1(ry)} 0 1 0 ${f1(-2 * rx)} 0Z`;
}

function rectD(x: number, y: number, w: number, h: number): string {
  return `M${pt(x, y)}h${f1(w)}v${f1(h)}h${f1(-w)}Z`;
}

/** Arche : rectangle coiffé d'un demi-cercle (portes, fenêtres, entrées). */
function archD(x: number, bottom: number, w: number, h: number): string {
  const r = w / 2;
  return `M${pt(x - r, bottom)}V${f1(bottom - h + r)}a${f1(r)} ${f1(r)} 0 0 1 ${f1(w)} 0V${f1(bottom)}Z`;
}

/** Créneaux posés sur une ligne de faîte. */
function crenelsD(x0: number, x1: number, y: number, w = 3.2, h = 2.8, gap = 2.6): string {
  let d = "";
  for (let x = x0; x + w <= x1 + 0.01; x += w + gap) d += rectD(x, y - h, w, h + 0.4);
  return d;
}

/** Flamme : goutte renversée posée en (x, y). */
function flameD(x: number, y: number, w: number, h: number): string {
  return `M${pt(x, y - h)}C${pt(x + w * 0.2, y - h * 0.62)} ${pt(x + w, y - h * 0.46)} ${pt(x + w * 0.8, y - h * 0.12)}Q${pt(x, y + h * 0.2)} ${pt(x - w * 0.8, y - h * 0.12)}C${pt(x - w, y - h * 0.46)} ${pt(x - w * 0.2, y - h * 0.62)} ${pt(x, y - h)}Z`;
}

/** Éventail de feuilles en amande partant d'un point (palmes, fougères). Angles : 0 = droite, π/2 = haut. */
function fanD(r: Prng, x: number, y: number, len: number, count: number, a0: number, a1: number, droop: number): string {
  let d = "";
  for (let i = 0; i < count; i++) {
    const a = a0 + ((a1 - a0) * (i + 0.5)) / count + r.range(-0.08, 0.08);
    const l = len * r.range(0.8, 1.1);
    const c = Math.cos(a);
    const s = Math.sin(a);
    const ex = x + c * l;
    const ey = y - s * l + droop * l * (1 - s);
    const mx = x + c * l * 0.5;
    const my = y - s * l * 0.5 + droop * l * (1 - s) * 0.3;
    const nx = -s * l * 0.14;
    const ny = -c * l * 0.14;
    d += `M${pt(x, y)}Q${pt(mx + nx, my + ny)} ${pt(ex, ey)}Q${pt(mx - nx, my - ny)} ${pt(x, y)}Z`;
  }
  return d;
}

/** Ordonnée d'un profil (points triés par x) à l'abscisse x. */
function yAt(pts: Pt[], x: number): number {
  if (x <= pts[0][0]) return pts[0][1];
  for (let i = 1; i < pts.length; i++) {
    if (x <= pts[i][0]) {
      const [ax, ay] = pts[i - 1];
      const [bx, by] = pts[i];
      return ay + ((by - ay) * (x - ax)) / Math.max(1e-6, bx - ax);
    }
  }
  return pts[pts.length - 1][1];
}

/** Animation CSS : durée, retard (négatif = déjà en route) et variables propres à l'élément. */
function anim(dur: number, delay: number, vars: Record<string, string> = {}): string {
  let s = `animation-duration:${f2(dur)}s;animation-delay:${f2(delay)}s`;
  for (const k of Object.keys(vars)) s += `;--${k}:${vars[k]}`;
  return s;
}

// ───────── Profils de relief ─────────

interface Ridge { pts: Pt[]; peaks: number[]; step: number }

/** Crête de montagnes : sommets alternés puis déplacement du point médian (dents de scie adoucies). */
function mountainRidge(r: Prng, base: number, amp: number, peaks: number, rough: number): Ridge {
  const n = peaks * 2;
  const span = 460 / n;
  let pts: Pt[] = [];
  for (let i = 0; i <= n; i++) {
    const x = -30 + i * span + (i > 0 && i < n ? r.range(-0.28, 0.28) * span : 0);
    const y = i % 2 ? base - amp * r.range(0.6, 1) : base - amp * r.range(0.12, 0.42);
    pts.push([x, y]);
  }
  const levels = 3;
  let disp = rough;
  for (let l = 0; l < levels; l++) {
    const next: Pt[] = [pts[0]];
    for (let i = 1; i < pts.length; i++) {
      const [ax, ay] = pts[i - 1];
      const [bx, by] = pts[i];
      const len = bx - ax;
      next.push([(ax + bx) / 2 + r.range(-0.1, 0.1) * len, (ay + by) / 2 + r.range(-1, 1) * disp * len]);
      next.push(pts[i]);
    }
    pts = next;
    disp *= 0.5;
  }
  const step = 2 ** levels;
  const peakIdx: number[] = [];
  for (let i = 1; i <= n; i += 2) peakIdx.push(i * step);
  return { pts, peaks: peakIdx, step };
}

/** Collines douces : somme de trois sinusoïdes. */
function hillPts(r: Prng, base: number, amp: number, waves: number, step = 8): Pt[] {
  const p1 = r.range(0, 6.283);
  const p2 = r.range(0, 6.283);
  const p3 = r.range(0, 6.283);
  const k1 = waves * r.range(0.85, 1.2);
  const k2 = k1 * r.range(1.9, 2.6);
  const k3 = k1 * r.range(3.6, 5.2);
  const pts: Pt[] = [];
  for (let x = -30; x <= 430; x += step) {
    const t = ((x + 30) / 460) * Math.PI;
    const v = 0.55 * Math.sin(t * k1 + p1) + 0.3 * Math.sin(t * k2 + p2) + 0.15 * Math.sin(t * k3 + p3);
    pts.push([x, base - amp * (0.5 + 0.5 * v)]);
  }
  return pts;
}

/** Mesas du désert : plateaux à flancs abrupts. */
function mesaPts(r: Prng, base: number, amp: number): Pt[] {
  const pts: Pt[] = [[-30, base - amp * 0.1]];
  let x = -30 + r.range(0, 30);
  while (x < 430) {
    x += r.range(18, 60);
    const w = r.range(30, 84);
    const h = amp * r.range(0.45, 1);
    pts.push([x, base - amp * r.range(0.04, 0.14)]);
    pts.push([x + w * 0.1, base - h * 0.7]);
    pts.push([x + w * 0.16, base - h]);
    pts.push([x + w * 0.86, base - h + r.range(-1.5, 1.5)]);
    pts.push([x + w * 0.92, base - h * 0.66]);
    pts.push([x + w, base - amp * r.range(0.04, 0.14)]);
    x += w;
  }
  pts.push([440, base - amp * 0.1]);
  return pts;
}

/** Dunes : face au vent en pente douce, face sous le vent abrupte et ombrée. */
function duneLayer(r: Prng, base: number, amp: number, count: number): { fill: string; top: string; shade: string } {
  let x = -30 - r.range(0, 40);
  let y0 = base - amp * r.range(0, 0.15);
  let top = `M${pt(x, y0)}`;
  let shade = "";
  while (x < 430) {
    const w = (460 / count) * r.range(0.75, 1.3);
    const h = amp * r.range(0.5, 1);
    const cx = x + w * r.range(0.58, 0.72);
    const cy = base - h;
    const ex = x + w;
    const ey = base - amp * r.range(0, 0.15);
    const down = `C${pt(cx + w * 0.07, cy + h * 0.32)} ${pt(ex - w * 0.14, ey)} ${pt(ex, ey)}`;
    top += `C${pt(x + w * 0.3, y0 - h * 0.1)} ${pt(cx - w * 0.22, cy + h * 0.06)} ${pt(cx, cy)}${down}`;
    shade += `M${pt(cx, cy)}${down}L${pt(ex - w * 0.05, base + 40)}L${pt(cx - w * 0.04, base + 40)}Z`;
    x = ex;
    y0 = ey;
  }
  return { fill: closeDown(top), top, shade };
}

/** Creuse un col en V autour de cx. */
function notch(pts: Pt[], cx: number, y: number, width: number): void {
  for (const q of pts) {
    const d = (q[0] - cx) / width;
    const w = Math.exp(-d * d);
    q[1] += (y - q[1]) * w;
  }
}

/** Relève les bords d'un premier plan pour cadrer la scène. */
function liftEdges(pts: Pt[], amount: number): void {
  for (const q of pts) {
    const e = Math.abs(q[0] - 200) / 200;
    const k = Math.max(0, e - 0.4) / 0.6;
    q[1] -= amount * k * k;
  }
}

/** Frange d'arbres minuscules le long d'une crête (forêts lointaines). */
function fringeD(r: Prng, pts: Pt[], kind: "sapins" | "feuillus", hmin: number, hmax: number, step: number): string {
  let d = "";
  for (let x = -26; x < 426; x += step * r.range(0.7, 1.3)) {
    const y = yAt(pts, x) + 1.5;
    const h = r.range(hmin, hmax);
    d += kind === "sapins" ? polyD([[x - h * 0.32, y], [x, y - h], [x + h * 0.32, y]]) : circleD(x, y - h * 0.35, h * 0.55);
  }
  return d;
}

// ───────── Palettes ─────────

interface BiomePalette { far: string; mid: string; near: string; albedo: number; tree: string; tree2: string; rock: string; accent: string }

const PAL: Record<Biome, BiomePalette> = {
  plaine: { far: "#7f9fb2", mid: "#6f9a52", near: "#4d7a35", albedo: 0, tree: "#3f6a34", tree2: "#56803f", rock: "#8d8a7c", accent: "#f2d36b" },
  foret: { far: "#6c8c84", mid: "#3f6a46", near: "#2a4c2f", albedo: 0, tree: "#24472e", tree2: "#36603a", rock: "#77756a", accent: "#e9f0a0" },
  montagne: { far: "#8e9bb3", mid: "#6e7486", near: "#4f5566", albedo: 0.15, tree: "#2e4a3c", tree2: "#3c5c48", rock: "#7c7f8a", accent: "#f4f7fb" },
  marais: { far: "#6f8072", mid: "#4e5d43", near: "#37432f", albedo: 0, tree: "#2f3a2a", tree2: "#465437", rock: "#5d5a4a", accent: "#b9e08a" },
  desert: { far: "#e6c08a", mid: "#dda865", near: "#c98d4b", albedo: 0.1, tree: "#4b7a3d", tree2: "#5f8f45", rock: "#b2733f", accent: "#ffe2a8" },
  cote: { far: "#7f9cb0", mid: "#8a8673", near: "#d6c193", albedo: 0.05, tree: "#36593b", tree2: "#4a7046", rock: "#7d7668", accent: "#eaf6fb" },
  toundra: { far: "#c9d5e3", mid: "#e2e9f1", near: "#f0f4f8", albedo: 0.7, tree: "#2b4236", tree2: "#3a5446", rock: "#5d6676", accent: "#ffffff" },
  souterrain: { far: "#5a4d72", mid: "#3d3452", near: "#2a2338", albedo: 0, tree: "#76629c", tree2: "#4f8496", rock: "#3d3550", accent: "#7cf3ff" },
  volcan: { far: "#5a4646", mid: "#352a2a", near: "#1d1717", albedo: 0, tree: "#2a2020", tree2: "#3a2a26", rock: "#2e2626", accent: "#ff6a1f" },
  jungle: { far: "#5a8a6a", mid: "#2f6b40", near: "#1e4b2b", albedo: 0, tree: "#1f5230", tree2: "#2f6e3d", rock: "#5a6352", accent: "#ff6f9a" },
};

const SKY: Record<ScenePhase, SkyStops> = {
  aube: ["#2a3566", "#6f5089", "#d98b9c", "#f7bd8c"],
  jour: ["#3b7cc6", "#6eabe2", "#a8d2f0", "#d9eef7"],
  crepuscule: ["#1c1742", "#4b2a66", "#a6456b", "#f2884a"],
  nuit: ["#03060f", "#081129", "#0e1c40", "#192a55"],
};
const CAVE: SkyStops = ["#0b0912", "#151121", "#211a30", "#2e2541"];
const FOG: Record<ScenePhase, string> = { aube: "#e3c3bb", jour: "#d3dbe1", crepuscule: "#a68597", nuit: "#344058" };
const SHADE: Record<ScenePhase, string> = { aube: "#2a2342", jour: "#1e2a24", crepuscule: "#221433", nuit: "#071029" };
const RIM: Record<ScenePhase, string> = { aube: "#ffc6a0", jour: "#fff7e0", crepuscule: "#ffa56e", nuit: "#b4c8ff" };
const LIGHT: Record<ScenePhase, number> = { aube: 0.66, jour: 1, crepuscule: 0.5, nuit: 0.24 };
const WEATHER_LIGHT: Record<SceneWeather, number> = { clair: 1, brume: 0.9, pluie: 0.78, neige: 0.92, orage: 0.68 };
const LAMP = "#ffd27a";
const LAMP_GLOW = "#ffb347";
const CRYSTALS = ["#7cf3ff", "#b388ff", "#7dffb5"];

// ───────── Environnement lumineux ─────────

interface BuildInput {
  biome: Biome; kind: LocationKind; seed: string; phase: ScenePhase; weather: SceneWeather;
  hour: number; moon: number; danger: 0 | 1 | 2; combat: boolean;
}

interface Env extends BuildInput {
  under: boolean; night: boolean; overcast: boolean; lamps: boolean;
  light: number; sky: SkyStops; fog: string; fogAmount: number; shade: string; rim: string; rimOpacity: number;
  /** perte de saturation des couleurs (la nuit, les teintes s'éteignent) */
  desat: number;
  sun: Pt | null; moonAt: Pt | null; lightFromLeft: boolean;
}

function sunPosition(h: number): Pt | null {
  if (h < 5.7 || h > 21.3) return null;
  const t = (h - 6) / 15;
  const s = Math.sin(Math.PI * Math.min(1, Math.max(0, t)));
  return [44 + 312 * t, 138 - 110 * Math.pow(s, 1.35)];
}

function moonPosition(h: number): Pt | null {
  const t = ((((h - 21) % 24) + 24) % 24) / 10; // 21 h → 0, 7 h → 1
  if (t > 1) return null;
  return [70 + 260 * t, 100 - 66 * Math.sin(Math.PI * t)];
}

function makeEnv(o: BuildInput): Env {
  const under = o.biome === "souterrain";
  const overcast = !under && (o.weather === "pluie" || o.weather === "orage" || o.weather === "neige");
  const night = o.phase === "nuit";
  let sky: SkyStops = under ? [...CAVE] : [...SKY[o.phase]];
  const recolor = (fn: (c: string, i: number) => string): SkyStops => [fn(sky[0], 0), fn(sky[1], 1), fn(sky[2], 2), fn(sky[3], 3)];
  if (!under) {
    if (o.weather === "pluie" || o.weather === "orage") {
      const storm = o.weather === "orage";
      const grey = night ? (storm ? "#0a0e16" : "#111723") : o.phase === "jour" ? (storm ? "#4a525e" : "#6c7682") : storm ? "#36323f" : "#58556a";
      sky = recolor((c) => mix(c, grey, storm ? 0.74 : 0.62));
    } else if (o.weather === "neige") {
      const grey = night ? "#262e40" : o.phase === "jour" ? "#c4ccd6" : "#9a94a8";
      sky = recolor((c) => mix(c, grey, 0.55));
    } else if (o.weather === "brume") {
      sky = recolor((c, i) => mix(c, FOG[o.phase], [0.12, 0.22, 0.38, 0.5][i]));
    }
    if (o.biome === "volcan") {
      sky = [mix(sky[0], "#1c0a0a", 0.25), mix(sky[1], "#4a1712", 0.28), mix(sky[2], "#a3331b", 0.32), mix(sky[3], "#e0521f", night ? 0.5 : 0.36)];
    }
    if (o.biome === "desert" && o.phase === "jour") {
      sky[2] = mix(sky[2], "#dfe9e4", 0.2);
      sky[3] = mix(sky[3], "#f6ddb0", 0.38);
    }
    if (o.biome === "toundra" && !overcast) sky[3] = mix(sky[3], "#d8f0f2", night ? 0.06 : 0.2);
  }
  if (o.danger > 0) {
    const t = o.danger === 2 ? 0.42 : 0.26;
    sky = [mix(sky[0], "#2a0716", t), mix(sky[1], "#3d0e22", t), mix(sky[2], "#5c1a27", t * 0.8), mix(sky[3], "#7a2a1e", t * 0.6)];
  }
  let light = under ? (o.phase === "jour" ? 0.44 : 0.38) : LIGHT[o.phase] * WEATHER_LIGHT[o.weather];
  if (night && !overcast && !under) light += 0.05 * ((1 - Math.cos(2 * Math.PI * o.moon)) / 2);
  if (o.danger > 0) light *= o.danger === 2 ? 0.86 : 0.93;
  const fog = under ? mix(sky[3], "#3a3150", 0.3) : o.weather === "brume" ? mix(sky[3], FOG[o.phase], 0.5) : mix(sky[3], sky[2], 0.25);
  const sun = under || night ? null : sunPosition(o.hour);
  const moonAt = under ? null : moonPosition(o.hour);
  const lightX = sun ? sun[0] : moonAt ? moonAt[0] : 140;
  return {
    ...o, under, night, overcast,
    lamps: under || o.phase !== "jour" || o.weather === "orage",
    light, sky, fog,
    fogAmount: under ? 0.45 : o.weather === "brume" ? 0.8 : overcast ? 0.68 : 0.6,
    shade: under ? "#060409" : SHADE[o.phase],
    rim: RIM[o.phase],
    rimOpacity: under || overcast ? 0 : night ? 0.22 : o.phase === "jour" ? 0.3 : 0.55,
    desat: (night ? 0.55 : o.phase === "crepuscule" ? 0.18 : o.phase === "aube" ? 0.12 : 0) + (overcast ? 0.2 : 0) + (under ? 0.15 : 0),
    sun, moonAt, lightFromLeft: lightX < 200,
  };
}

// ───────── Peintre : accumule les <defs> et les couches dans l'ordre ─────────

class Painter {
  readonly defs: El[] = [];
  readonly out: El[] = [];
  private n = 0;
  private known = new Set<string>();
  constructor(readonly env: Env, readonly uid: string) {}
  id(name: string): string {
    return `${this.uid}-${name}`;
  }
  key(): number {
    return this.n++;
  }
  add(el: El): void {
    this.out.push(el);
  }
  /** Déclare une définition une seule fois et renvoie son url(#…). */
  def(name: string, make: (id: string) => El): string {
    if (!this.known.has(name)) {
      this.known.add(name);
      this.defs.push(make(this.id(name)));
    }
    return `url(#${this.id(name)})`;
  }
  /** Halo radial réutilisable (fenêtres, torches, cristaux, lucioles…). */
  glow(color: string, core = 0.9): string {
    return this.def(`halo-${color.slice(1)}-${Math.round(core * 100)}`, (id) => (
      <radialGradient id={id}>
        <stop offset="0" stop-color={color} stop-opacity={f2(core)} />
        <stop offset="0.35" stop-color={color} stop-opacity={f2(core * 0.4)} />
        <stop offset="1" stop-color={color} stop-opacity="0" />
      </radialGradient>
    ));
  }
  /** Couleur d'un élément à une profondeur donnée (0 = horizon, 1 = premier plan) : brume lointaine puis ombre de l'heure. */
  tone(base: string, depth: number, albedo = 0): string {
    const e = this.env;
    let c = mix(base, e.fog, e.fogAmount * Math.pow(1 - depth, 1.25));
    if (e.desat > 0) c = desaturate(c, Math.min(1, e.desat * (0.55 + 0.45 * depth)));
    return mix(c, e.shade, (1 - e.light) * (0.5 + 0.45 * depth) * (1 - 0.5 * albedo));
  }
  batch(b: Batch): void {
    if (!b.empty) this.add(<g key={this.key()}>{b.els()}</g>);
  }
}

/** Regroupe les tracés de même couleur en un seul <path> (beaucoup moins de nœuds DOM). */
class Batch {
  private parts = new Map<string, string>();
  fill(color: string, d: string, opacity = 1): void {
    this.push(`f|${color}|${f2(opacity)}`, d);
  }
  stroke(color: string, width: number, d: string, opacity = 1): void {
    this.push(`s|${color}|${f2(width)}|${f2(opacity)}`, d);
  }
  private push(k: string, d: string): void {
    this.parts.set(k, (this.parts.get(k) ?? "") + d);
  }
  get empty(): boolean {
    return this.parts.size === 0;
  }
  els(): El[] {
    const out: El[] = [];
    let i = 0;
    for (const [k, d] of this.parts) {
      const [t, color, a, b] = k.split("|");
      out.push(t === "f"
        ? <path key={i++} d={d} fill={color} opacity={a === "1" ? undefined : a} />
        : <path key={i++} d={d} fill="none" stroke={color} stroke-width={a} opacity={b === "1" ? undefined : b} stroke-linecap="round" stroke-linejoin="round" />);
    }
    return out;
  }
}

// ───────── Arbres ─────────

type TreeKind = "sapin" | "feuillu" | "palmier" | "mort" | "cactus" | "saule" | "pin" | "tropical" | "champignon";
interface TreeColors { crown: string; trunk: string; snow?: string; light?: string; accent: string }

const TREES: Record<Biome, TreeKind[]> = {
  plaine: ["feuillu", "feuillu", "sapin"],
  foret: ["sapin", "feuillu", "sapin"],
  montagne: ["sapin"],
  marais: ["saule", "mort", "mort"],
  desert: ["palmier", "palmier", "cactus"],
  cote: ["pin", "feuillu", "palmier"],
  toundra: ["sapin", "sapin", "mort"],
  souterrain: ["champignon"],
  volcan: ["mort"],
  jungle: ["tropical", "palmier", "tropical"],
};

function treeColors(p: Painter, depth: number, alt = false): TreeColors {
  const e = p.env;
  const pal = PAL[e.biome];
  const crown = p.tone(alt ? pal.tree2 : pal.tree, depth);
  return {
    crown,
    trunk: p.tone(e.biome === "volcan" ? "#1c1414" : e.biome === "souterrain" ? "#5d5470" : "#4a3626", depth),
    snow: pal.albedo >= 0.5 || e.weather === "neige" ? p.tone("#f2f6fa", depth, 1) : undefined,
    light: e.rimOpacity > 0 && !e.night ? mix(crown, e.rim, 0.22) : undefined,
    accent: e.biome === "souterrain" ? pal.accent : p.tone(pal.accent, depth * 0.6),
  };
}

function firD(x: number, base: number, h: number, w: number): string {
  const t = base - h;
  return polyD([
    [x, t],
    [x + w * 0.28, t + h * 0.34], [x + w * 0.14, t + h * 0.34],
    [x + w * 0.4, t + h * 0.64], [x + w * 0.2, t + h * 0.64],
    [x + w * 0.5, t + h * 0.92], [x + w * 0.07, t + h * 0.92], [x + w * 0.07, base],
    [x - w * 0.07, base], [x - w * 0.07, t + h * 0.92], [x - w * 0.5, t + h * 0.92],
    [x - w * 0.2, t + h * 0.64], [x - w * 0.4, t + h * 0.64],
    [x - w * 0.14, t + h * 0.34], [x - w * 0.28, t + h * 0.34],
  ]);
}

function firSnowD(x: number, base: number, h: number, w: number): string {
  const t = base - h;
  let d = polyD([[x, t], [x + w * 0.2, t + h * 0.25], [x + w * 0.06, t + h * 0.2], [x, t + h * 0.23], [x - w * 0.06, t + h * 0.2], [x - w * 0.2, t + h * 0.25]]);
  for (const s of [1, -1]) {
    d += polyD([[x + s * w * 0.14, t + h * 0.34], [x + s * w * 0.4, t + h * 0.64], [x + s * w * 0.31, t + h * 0.61], [x + s * w * 0.15, t + h * 0.42]]);
    d += polyD([[x + s * w * 0.2, t + h * 0.64], [x + s * w * 0.5, t + h * 0.92], [x + s * w * 0.4, t + h * 0.89], [x + s * w * 0.21, t + h * 0.72]]);
  }
  return d;
}

function addTree(b: Batch, r: Prng, kind: TreeKind, x: number, base: number, h: number, c: TreeColors, lightLeft: boolean): void {
  const side = lightLeft ? -1 : 1;
  switch (kind) {
    case "sapin": {
      const w = h * r.range(0.42, 0.56);
      b.fill(c.crown, firD(x, base, h, w));
      if (c.snow) b.fill(c.snow, firSnowD(x, base, h, w));
      return;
    }
    case "feuillu":
    case "tropical": {
      const tropical = kind === "tropical";
      const trunkH = h * (tropical ? 0.5 : 0.4);
      const cr = h * (tropical ? 0.25 : 0.3);
      b.stroke(c.trunk, q4(Math.max(0.8, h * 0.06)), `M${pt(x, base)}Q${pt(x + r.range(-2, 2), base - trunkH * 0.6)} ${pt(x + r.range(-1.5, 1.5), base - trunkH - cr * 0.3)}`);
      const cy = base - trunkH - cr * 0.5;
      let d = circleD(x, cy, cr) + circleD(x - cr * 0.7, cy + cr * 0.3, cr * 0.7) + circleD(x + cr * 0.68, cy + cr * 0.28, cr * 0.74);
      d += circleD(x + r.range(-0.3, 0.3) * cr, cy - cr * 0.6, cr * 0.7);
      if (tropical) d += circleD(x - cr * 1.25, cy + cr * 0.45, cr * 0.5) + circleD(x + cr * 1.25, cy + cr * 0.4, cr * 0.52);
      b.fill(c.crown, d);
      if (c.light) b.fill(c.light, circleD(x + side * cr * 0.35, cy - cr * 0.4, cr * 0.46), 0.85);
      if (c.snow) b.fill(c.snow, `M${pt(x - cr * 0.95, cy - cr * 0.3)}Q${pt(x, cy - cr * 1.75)} ${pt(x + cr * 0.95, cy - cr * 0.3)}Q${pt(x, cy - cr * 0.95)} ${pt(x - cr * 0.95, cy - cr * 0.3)}Z`);
      return;
    }
    case "palmier": {
      const lean = r.range(-0.28, 0.28) * h;
      const tx = x + lean;
      const ty = base - h;
      b.stroke(c.trunk, q4(Math.max(0.9, h * 0.055)), `M${pt(x, base)}Q${pt(x + lean * 0.15, base - h * 0.55)} ${pt(tx, ty)}`);
      b.fill(c.crown, fanD(r, tx, ty, h * 0.42, 7, 0.12, Math.PI - 0.12, 0.5));
      return;
    }
    case "mort": {
      const lean = r.range(-0.15, 0.15) * h;
      let br = "";
      const n = r.int(3, 5);
      for (let i = 0; i < n; i++) {
        const t = r.range(0.38, 0.92);
        const bx = x + lean * t * t;
        const by = base - h * t;
        const dir = i % 2 ? 1 : -1;
        const len = h * r.range(0.2, 0.36) * (1.15 - t * 0.5);
        const ex = bx + dir * len;
        const ey = by - len * r.range(0.45, 0.9);
        br += `M${pt(bx, by)}Q${pt(bx + dir * len * 0.5, by - len * 0.1)} ${pt(ex, ey)}`;
        if (r.chance(0.6)) {
          const mx = bx + dir * len * 0.6;
          const my = by - len * 0.32;
          br += `M${pt(mx, my)}L${pt(mx + dir * len * 0.22, my - len * 0.45)}`;
        }
      }
      b.stroke(c.trunk, q4(Math.max(0.9, h * 0.06)), `M${pt(x, base)}Q${pt(x + lean * 0.3, base - h * 0.5)} ${pt(x + lean, base - h)}`);
      b.stroke(c.trunk, q4(Math.max(0.5, h * 0.025)), br);
      return;
    }
    case "cactus": {
      let d = `M${pt(x, base)}V${f1(base - h)}`;
      if (r.chance(0.85)) {
        const ay = base - h * r.range(0.32, 0.5);
        const ax = x - h * r.range(0.18, 0.26);
        d += `M${pt(x, ay)}H${f1(ax)}V${f1(ay - h * r.range(0.2, 0.32))}`;
      }
      if (r.chance(0.7)) {
        const ay = base - h * r.range(0.42, 0.62);
        const ax = x + h * r.range(0.16, 0.24);
        d += `M${pt(x, ay)}H${f1(ax)}V${f1(ay - h * r.range(0.18, 0.26))}`;
      }
      b.stroke(c.crown, q4(Math.max(1.4, h * 0.15)), d);
      return;
    }
    case "saule": {
      const trunkH = h * 0.45;
      const cr = h * 0.3;
      b.stroke(c.trunk, q4(Math.max(0.9, h * 0.07)), `M${pt(x, base)}Q${pt(x + 2, base - trunkH * 0.5)} ${pt(x, base - trunkH - cr * 0.2)}`);
      const cy = base - trunkH - cr * 0.4;
      b.fill(c.crown, ellipseD(x, cy, cr * 1.3, cr * 0.75) + circleD(x - cr * 0.5, cy - cr * 0.35, cr * 0.6) + circleD(x + cr * 0.5, cy - cr * 0.3, cr * 0.62));
      let strands = "";
      for (let i = 0; i < 9; i++) {
        const sx = x - cr * 1.2 + (cr * 2.4 * i) / 8;
        const len = h * r.range(0.25, 0.45);
        strands += `M${pt(sx, cy + cr * 0.3)}q${f1(r.range(-1.5, 1.5))} ${f1(len * 0.5)} ${f1(r.range(-1, 1))} ${f1(len)}`;
      }
      b.stroke(c.crown, q4(Math.max(0.6, h * 0.025)), strands);
      return;
    }
    case "pin": {
      const lean = r.range(-0.3, 0.3) * h;
      const tx = x + lean;
      const ty = base - h * 0.8;
      b.stroke(c.trunk, q4(Math.max(0.8, h * 0.05)), `M${pt(x, base)}Q${pt(x + lean * 0.1, base - h * 0.5)} ${pt(tx, ty)}`);
      b.fill(c.crown, ellipseD(tx, ty - h * 0.06, h * 0.34, h * 0.1) + ellipseD(tx - h * 0.18, ty + h * 0.02, h * 0.2, h * 0.08) + ellipseD(tx + h * 0.2, ty, h * 0.22, h * 0.08));
      if (c.light) b.fill(c.light, ellipseD(tx + side * h * 0.08, ty - h * 0.11, h * 0.16, h * 0.04), 0.85);
      return;
    }
    case "champignon": {
      const stemH = h * 0.75;
      const cw = h * r.range(0.32, 0.45);
      b.stroke(c.trunk, q4(Math.max(1, h * 0.1)), `M${pt(x, base)}Q${pt(x + r.range(-3, 3), base - stemH * 0.5)} ${pt(x, base - stemH)}`);
      const cy = base - stemH;
      b.fill(c.crown, `M${pt(x - cw, cy + 1)}Q${pt(x - cw * 0.9, cy - h * 0.35)} ${pt(x, cy - h * 0.36)}Q${pt(x + cw * 0.9, cy - h * 0.35)} ${pt(x + cw, cy + 1)}Q${pt(x, cy - h * 0.06)} ${pt(x - cw, cy + 1)}Z`);
      let dots = "";
      for (let i = 0; i < 4; i++) dots += circleD(x + r.range(-0.6, 0.6) * cw, cy - h * r.range(0.1, 0.26), r.range(0.5, 1.1));
      b.fill(c.accent, dots);
      return;
    }
  }
}

/** Rangée d'arbres posée le long d'un profil. */
function treeRow(p: Painter, r: Prng, pts: Pt[] | null, o: { y?: number; depth: number; hmin: number; hmax: number; step: number; kinds: TreeKind[]; skip?: (x: number) => boolean; x0?: number; x1?: number }): void {
  const b = new Batch();
  const c1 = treeColors(p, o.depth);
  const c2 = treeColors(p, o.depth, true);
  for (let x = o.x0 ?? -20; x < (o.x1 ?? 420); x += o.step * r.range(0.6, 1.4)) {
    if (o.skip?.(x)) continue;
    const y = (pts ? yAt(pts, x) : o.y ?? 160) + r.range(1, 3);
    addTree(b, r, r.pick(o.kinds), x, y, r.range(o.hmin, o.hmax), r.chance(0.5) ? c1 : c2, p.env.lightFromLeft);
  }
  p.batch(b);
}

// ───────── Ciel ─────────

function paintSky(p: Painter): void {
  const e = p.env;
  const [a, b, c, d] = e.sky;
  const fill = p.def("ciel", (id) => (
    <linearGradient id={id} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2={e.under ? "200" : "134"}>
      <stop offset="0" stop-color={a} />
      <stop offset="0.42" stop-color={b} />
      <stop offset="0.78" stop-color={c} />
      <stop offset="1" stop-color={d} />
    </linearGradient>
  ));
  p.add(<rect key={p.key()} x="-20" y="-20" width="440" height="240" fill={fill} />);
}

function paintStars(p: Painter): void {
  const e = p.env;
  if (e.under || e.overcast) return;
  let amount = 0;
  if (e.phase === "nuit") amount = 1;
  else if (e.phase === "crepuscule" && e.hour >= 20.5) amount = 0.35;
  else if (e.phase === "aube" && e.hour < 6.6) amount = 0.3;
  if (e.weather === "brume") amount *= 0.45;
  if (amount <= 0) return;
  const r = rngFor("etoiles", e.seed);
  const els: El[] = [];
  if (amount >= 1 && e.weather === "clair" && r.chance(0.75)) {
    const band = p.def("voie-lactee", (id) => (
      <radialGradient id={id}>
        <stop offset="0" stop-color="#d6defc" stop-opacity="0.12" />
        <stop offset="0.5" stop-color="#b9c6f2" stop-opacity="0.05" />
        <stop offset="1" stop-color="#b9c6f2" stop-opacity="0" />
      </radialGradient>
    ));
    const tilt = r.range(-28, -12);
    els.push(<ellipse key="vl" cx="200" cy="52" rx="250" ry="24" fill={band} transform={`rotate(${f1(tilt)} 200 52)`} />);
    let dust = "";
    const rad = (tilt * Math.PI) / 180;
    for (let i = 0; i < 46; i++) {
      const t = r.range(-1, 1);
      const off = r.range(-1, 1) * r.range(0, 14);
      const x = 200 + t * 230 * Math.cos(rad) - off * Math.sin(rad);
      const y = 52 + t * 230 * Math.sin(rad) + off * Math.cos(rad);
      if (y > 2 && y < 118) dust += circleD(x, y, r.range(0.2, 0.42));
    }
    els.push(<path key="vl-p" d={dust} fill="#e8edff" opacity="0.55" />);
  }
  const count = Math.round(70 * amount);
  let still = "";
  let dim = "";
  let sparkle = "";
  for (let i = 0; i < count; i++) {
    const x = r.range(0, 400);
    const y = 2 + 112 * Math.pow(r.next(), 1.5);
    const big = r.chance(0.1);
    const rad = big ? r.range(0.9, 1.3) : r.range(0.35, 0.8);
    if (r.chance(0.45)) {
      const op = (big ? 0.95 : r.range(0.5, 0.9)) * Math.min(1, amount + 0.2);
      const dur = r.range(2.4, 6.5);
      els.push(
        <circle key={`t${i}`} class="scene-twinkle" cx={f1(x)} cy={f1(y)} r={f1(rad)} fill={r.pick(["#ffffff", "#dfe8ff", "#fff1d6"])}
          opacity={f2(op)} style={anim(dur, -r.range(0, dur), { o: f2(op) })} />,
      );
    } else if (r.chance(0.5)) still += circleD(x, y, rad);
    else dim += circleD(x, y, rad * 0.8);
    if (big && sparkle.length < 400) sparkle += `M${pt(x - rad * 3, y)}H${f1(x + rad * 3)}M${pt(x, y - rad * 3)}V${f1(y + rad * 3)}`;
  }
  els.push(<path key="s1" d={still} fill="#ffffff" opacity={f2(0.85 * amount)} />);
  els.push(<path key="s2" d={dim} fill="#cfdaff" opacity={f2(0.5 * amount)} />);
  if (sparkle) els.push(<path key="s3" d={sparkle} stroke="#ffffff" stroke-width="0.3" opacity={f2(0.55 * amount)} />);
  if (amount >= 1 && e.weather === "clair" && r.chance(0.7)) {
    const sx = r.range(170, 340);
    const sy = r.range(14, 44);
    const trail = p.def("filante", (id) => (
      <linearGradient id={id} x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stop-color="#ffffff" stop-opacity="0" />
        <stop offset="1" stop-color="#ffffff" stop-opacity="0.95" />
      </linearGradient>
    ));
    els.push(
      <g key="filante" class="scene-shooting" style={anim(r.range(9, 14), -r.range(0, 8))}>
        <path d={`M${pt(sx + 22, sy - 11)}L${pt(sx, sy)}`} stroke={trail} stroke-width="0.9" stroke-linecap="round" />
      </g>,
    );
  }
  p.add(<g key={p.key()}>{els}</g>);
}

function paintAurora(p: Painter): void {
  const e = p.env;
  if (e.under || e.phase !== "nuit" || e.weather !== "clair") return;
  const r = rngFor("aurore", e.seed);
  if (!(e.biome === "toundra" || (e.biome === "montagne" && r.chance(0.3)))) return;
  const els: El[] = [];
  for (let i = 0; i < 2; i++) {
    const y = 34 + i * 18 + r.range(-4, 4);
    const amp = r.range(6, 12);
    const k = r.range(1.4, 2.6);
    const ph = r.range(0, 6.28);
    const curve: Pt[] = [];
    for (let x = -20; x <= 420; x += 20) curve.push([x, y + Math.sin((x / 400) * Math.PI * k + ph) * amp]);
    const d = smoothD(curve);
    // rideau : rayons verticaux qui s'effacent vers le haut
    const rays = p.def(`aurore-${i}`, (id) => (
      <linearGradient id={id} gradientUnits="userSpaceOnUse" x1="0" y1={f1(y - amp - 34)} x2="0" y2={f1(y + amp)}>
        <stop offset="0" stop-color="#a98bff" stop-opacity="0" />
        <stop offset="0.55" stop-color="#7dffc4" stop-opacity="0.12" />
        <stop offset="1" stop-color="#5dffb0" stop-opacity="0.32" />
      </linearGradient>
    ));
    // trois voiles de rayons d'épaisseurs différentes, espacés au hasard : pas d'effet de peigne
    const veils = [
      { w: 6, o: 0.35, gap: [9, 20] },
      { w: 3, o: 0.5, gap: [6, 14] },
      { w: 1.4, o: 0.6, gap: [5, 12] },
    ].map((v, j) => {
      let dd = "";
      for (let x = -16 + r.range(0, 8); x <= 416; x += r.range(v.gap[0], v.gap[1])) {
        const len = r.range(10, 36) * (0.6 + 0.4 * Math.sin(x / 31 + ph + j));
        dd += `M${pt(x, yAt(curve, x) + 2)}v${f1(-Math.max(6, len))}`;
      }
      return <path key={j} d={dd} stroke={rays} stroke-width={f1(v.w)} opacity={f2(v.o)} fill="none" stroke-linecap="round" />;
    });
    els.push(
      <g key={i} class="scene-aurora" style={anim(r.range(10, 18), -r.range(0, 10))}>
        {veils}
        <path d={d} fill="none" stroke="#5dffb0" stroke-width="16" opacity="0.07" stroke-linecap="round" />
        <path d={d} fill="none" stroke="#7dffc4" stroke-width="6" opacity="0.12" stroke-linecap="round" />
      </g>,
    );
  }
  p.add(<g key={p.key()} opacity="0.85">{els}</g>);
}

function paintSun(p: Painter): void {
  const e = p.env;
  if (e.under || !e.sun || e.night) return;
  const [x, y] = e.sun;
  const tones: Record<"aube" | "jour" | "crepuscule", [string, string, string]> = {
    aube: ["#fff0d6", "#ffb08a", "#ff8f6b"],
    jour: ["#fffdf2", "#fff1c2", "#ffe9a8"],
    crepuscule: ["#ffd29a", "#ff8a4c", "#ff5a3a"],
  };
  const [core, halo, wide] = tones[e.phase === "nuit" ? "crepuscule" : e.phase];
  const els: El[] = [];
  if (e.phase !== "jour") els.push(<ellipse key="hz" cx={f1(x)} cy="128" rx="200" ry="48" fill={p.glow(wide, e.overcast ? 0.3 : 0.6)} />);
  els.push(<circle key="g1" cx={f1(x)} cy={f1(y)} r="72" fill={p.glow(halo, e.overcast ? 0.28 : 0.5)} />);
  if (!e.overcast) {
    els.push(<circle key="g2" cx={f1(x)} cy={f1(y)} r="20" fill={p.glow(core, 0.85)} />);
    els.push(<circle key="c" cx={f1(x)} cy={f1(y)} r={e.phase === "jour" ? "8" : "10.5"} fill={core} opacity={e.weather === "brume" ? "0.7" : "1"} />);
  }
  p.add(<g key={p.key()}>{els}</g>);
}

/** Croissant ou gibbeuse : demi-cercle éclairé + terminateur elliptique. */
function moonLitD(cx: number, cy: number, r: number, phase: number): string {
  const k = Math.cos(2 * Math.PI * phase);
  const rx = Math.max(0.01, Math.abs(k) * r);
  const waxing = phase < 0.5;
  const outer = waxing ? 1 : 0;
  const term = waxing === k > 0 ? 0 : 1;
  return `M${pt(cx, cy - r)}A${f1(r)} ${f1(r)} 0 0 ${outer} ${pt(cx, cy + r)}A${f2(rx)} ${f1(r)} 0 0 ${term} ${pt(cx, cy - r)}Z`;
}

function paintMoon(p: Painter): void {
  const e = p.env;
  if (e.under || !e.night || !e.moonAt) return;
  const [x, y] = e.moonAt;
  const blood = e.danger === 2;
  const illum = (1 - Math.cos(2 * Math.PI * e.moon)) / 2;
  const lit = blood ? "#f2a184" : "#f4eedb";
  const els: El[] = [<circle key="g" cx={f1(x)} cy={f1(y)} r="50" fill={p.glow(blood ? "#ff6a4a" : "#c9d8ff", 0.12 + 0.3 * illum)} opacity={e.overcast ? "0.45" : "1"} />];
  if (!e.overcast) {
    const r = 8.5;
    const litD = moonLitD(x, y, r, e.moon);
    const clip = p.def("lune", (id) => <clipPath id={id}><path d={litD} /></clipPath>);
    els.push(<circle key="d" cx={f1(x)} cy={f1(y)} r={f1(r)} fill={mix(e.sky[1], "#5b6684", 0.4)} opacity="0.55" />);
    els.push(<path key="l" d={litD} fill={lit} opacity={e.weather === "brume" ? "0.75" : "1"} />);
    els.push(
      <g key="cr" clip-path={clip} opacity="0.2" fill="#8a7f6a">
        <circle cx={f1(x - 2.6)} cy={f1(y - 2)} r="2.1" />
        <circle cx={f1(x + 2.8)} cy={f1(y + 1.6)} r="1.6" />
        <circle cx={f1(x - 0.6)} cy={f1(y + 4)} r="1.1" />
        <circle cx={f1(x + 1.4)} cy={f1(y - 4.4)} r="0.9" />
      </g>,
    );
  }
  p.add(<g key={p.key()}>{els}</g>);
}

/** Nuage étiré : base aplatie et rangée de bosses, plus hautes vers le centre. */
function cloudD(r: Prng, cx: number, cy: number, w: number, h: number): string {
  const n = Math.max(4, Math.round(w / (h * 0.55)));
  let d = ellipseD(cx, cy + h * 0.16, w * 0.52, h * 0.28);
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    const rad = h * (0.26 + 0.3 * Math.sin(Math.PI * (0.08 + 0.84 * t))) * r.range(0.82, 1.15);
    d += circleD(cx - w * 0.44 + w * 0.88 * t + r.range(-2, 2), cy + h * 0.14 - rad * 0.62, rad);
  }
  return d;
}

function cloudColors(e: Env): [string, string, number] {
  if (e.weather === "orage") return e.night ? ["#1b2130", "#0b0f18", 0.96] : ["#5a616d", "#2f343d", 0.96];
  if (e.weather === "pluie") return e.night ? ["#222a3b", "#111723", 0.93] : ["#8d96a2", "#59616d", 0.93];
  if (e.weather === "neige") return e.night ? ["#2f3849", "#1c2331", 0.9] : ["#e1e6ec", "#aab3be", 0.9];
  if (e.danger === 2) return e.night ? ["#2a1422", "#140a12", 0.8] : ["#b07a7a", "#5a2a33", 0.85];
  switch (e.phase) {
    case "aube": return ["#ffe0cc", "#b0759a", 0.85];
    case "jour": return ["#ffffff", "#c9d9e8", 0.9];
    case "crepuscule": return ["#ffb489", "#5d2f63", 0.85];
    default: return ["#27314f", "#121a33", 0.7];
  }
}

function paintClouds(p: Painter): void {
  const e = p.env;
  if (e.under) return;
  const r = rngFor("nuages", e.seed, e.weather, e.phase);
  const [lit, dark, op] = cloudColors(e);
  const fill = p.def("nuage", (id) => (
    <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color={lit} />
      <stop offset="1" stop-color={dark} />
    </linearGradient>
  ));
  const els: El[] = [];
  if (e.overcast) {
    els.push(<rect key="voile" x="-20" y="-20" width="440" height="44" fill={dark} opacity={f2(op)} />);
    const n = r.int(7, 9);
    for (let i = 0; i < n; i++) {
      const w = r.range(90, 150);
      const cx = -30 + (460 * (i + r.range(0.2, 0.8))) / n;
      const cy = r.range(14, 40);
      const dur = r.range(60, 110);
      els.push(<path key={i} class="scene-drift" d={cloudD(r, cx, cy, w, w * 0.32)} fill={fill} opacity={f2(op)} style={anim(dur, -r.range(0, dur), { mx: px(r.range(8, 20)) })} />);
    }
    for (let i = 0; i < 3; i++) {
      const w = r.range(70, 120);
      els.push(<path key={`b${i}`} d={cloudD(r, r.range(20, 380), r.range(48, 64), w, w * 0.25)} fill={fill} opacity={f2(op * 0.55)} />);
    }
  } else {
    const n = e.weather === "brume" ? r.int(1, 2) : e.night ? r.int(0, 2) : r.int(1, 3);
    for (let i = 0; i < n; i++) {
      const w = r.range(46, 96);
      const cx = r.range(30, 370);
      const cy = r.range(22, 72);
      const dur = r.range(70, 130);
      els.push(<path key={i} class="scene-drift" d={cloudD(r, cx, cy, w, w * r.range(0.28, 0.36))} fill={fill} opacity={f2(op * (e.weather === "brume" ? 0.6 : 1))} style={anim(dur, -r.range(0, dur), { mx: px(r.range(14, 30)) })} />);
    }
  }
  if (els.length) p.add(<g key={p.key()}>{els}</g>);
}

function boltD(r: Prng, x: number): string {
  let d = `M${pt(x, 34)}`;
  let cx = x;
  let branch = "";
  for (let i = 1; i <= 6; i++) {
    cx += r.range(-11, 11);
    const y = 34 + i * 14 + r.range(-3, 3);
    d += `L${pt(cx, y)}`;
    if (i === 2 || (i === 4 && r.chance(0.5))) branch += `M${pt(cx, y)}L${pt(cx + r.range(8, 16) * (r.chance(0.5) ? 1 : -1), y + r.range(10, 18))}`;
  }
  return d + branch;
}

/** Éclairs d'orage : derrière le relief (zébrure) et devant (lueur sur tout le paysage), mêmes temps. */
function stormTiming(e: Env): { dur: number; delay: number; x: number; d: string } {
  const r = rngFor("eclair", e.seed, Math.floor(e.hour));
  const x = r.range(90, 310);
  return { dur: r.range(7, 11), delay: -r.range(0, 6), x, d: boltD(r, x) };
}

function paintBolt(p: Painter): void {
  const e = p.env;
  if (e.weather !== "orage" || e.under) return;
  const s = stormTiming(e);
  p.add(
    <g key={p.key()} class="scene-flash" style={anim(s.dur, s.delay)}>
      <rect x="-20" y="-20" width="440" height="160" fill="#dfe7ff" opacity="0.35" />
      <path d={s.d} fill="none" stroke="#b9c8ff" stroke-width="4" opacity="0.35" stroke-linejoin="round" />
      <path d={s.d} fill="none" stroke="#ffffff" stroke-width="1.3" stroke-linejoin="round" />
    </g>,
  );
}

// ───────── Relief et eau ─────────

type WaterMode = "mer" | "lac" | null;
interface Spark { x: number; y: number; spread: number; count: number; rise: number; colors: string[] }
interface Stage {
  sx: number; base: number; water: WaterMode; waterY: number;
  reflect: { d: string; fill: string }[];
  embers: Spark[]; motes: Spark[];
  mist: boolean; bats: boolean; saddleY: number; cliffTop: Pt | null;
}

interface LayerOpts { albedo?: number; snowY?: number; rim?: boolean; texture?: string; peaks?: Ridge; reflect?: Stage; smooth?: boolean }

/** Plan de relief : dégradé vertical (vallées embrumées), ombre des flancs, neiges éternelles, liseré de lumière. */
function drawLayerD(p: Painter, name: string, top: string, fillD: string, depth: number, base: string, o: LayerOpts = {}): string {
  const e = p.env;
  const c = p.tone(base, depth, o.albedo ?? 0);
  const valley = mix(c, e.fog, 0.16 + 0.32 * (1 - depth) + (e.weather === "brume" ? 0.2 : 0));
  const fill = p.def(`couche-${name}`, (id) => (
    <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color={c} />
      <stop offset="1" stop-color={valley} />
    </linearGradient>
  ));
  const d = fillD + (o.texture ?? "");
  p.add(<path key={p.key()} d={d} fill={fill} />);
  o.reflect?.reflect.push({ d, fill: c });
  if (o.snowY !== undefined) {
    const r = rngFor("neiges", e.seed, name);
    let clip = "M-30 -30L430 -30";
    for (let x = 430; x >= -30; x -= 9) clip += `L${pt(x, o.snowY + r.range(-5, 7))}`;
    const cid = p.def(`neige-${name}`, (id) => <clipPath id={id}><path d={`${clip}Z`} /></clipPath>);
    p.add(<path key={p.key()} d={fillD} fill={p.tone("#f4f7fb", depth, 1)} clip-path={cid} opacity="0.92" />);
  }
  if (o.peaks) {
    const { pts, peaks, step } = o.peaks;
    let sh = "";
    for (const pk of peaks) {
      const a = e.lightFromLeft ? pk : pk - step;
      const b = e.lightFromLeft ? pk + step : pk;
      if (a < 0 || b >= pts.length) continue;
      // flanc à l'ombre : de la crête jusqu'au creux, refermé par l'arête qui descend du sommet (pas de bord vertical)
      const peak = e.lightFromLeft ? pts[a] : pts[b];
      const valley = e.lightFromLeft ? pts[b] : pts[a];
      const foot: Pt = [peak[0] + (valley[0] - peak[0]) * 0.32, valley[1] + (215 - valley[1]) * 0.22];
      sh += polyD([...pts.slice(a, b + 1), foot]);
    }
    if (sh) p.add(<path key={p.key()} d={sh} fill={e.shade} opacity={e.night ? "0.16" : "0.2"} />);
  }
  if (o.rim !== false && e.rimOpacity > 0) {
    p.add(<path key={p.key()} d={top} fill="none" stroke={e.rim} stroke-width="0.7" opacity={f2(e.rimOpacity * (0.45 + 0.55 * (1 - depth)))} />);
  }
  return d;
}

function drawLayer(p: Painter, name: string, pts: Pt[], depth: number, base: string, o: LayerOpts = {}): string {
  const top = o.smooth ? smoothD(pts) : polyD(pts, false);
  return drawLayerD(p, name, top, closeDown(top), depth, base, o);
}

/** Neiges éternelles plausibles sur les sommets de ce biome ? */
const hasSnow = (b: Biome): boolean => b !== "desert" && b !== "volcan" && b !== "jungle" && b !== "souterrain";

function paintFar(p: Painter, r: Prng, st: Stage): void {
  const e = p.env;
  const pal = PAL[e.biome];
  const o: LayerOpts = { reflect: st };
  if (e.kind === "col") {
    const rg = mountainRidge(r, 128, 80, 3, 0.18);
    notch(rg.pts, st.sx, 112, 30);
    st.saddleY = yAt(rg.pts, st.sx);
    drawLayer(p, "loin", rg.pts, 0.15, pal.albedo > 0.3 ? pal.far : mix(pal.far, PAL.montagne.far, 0.6), { ...o, snowY: hasSnow(e.biome) ? 84 : undefined, peaks: rg, albedo: Math.max(0.15, pal.albedo) });
    return;
  }
  switch (e.biome) {
    case "montagne": {
      const rg = mountainRidge(r, 128, 74, 4, 0.2);
      drawLayer(p, "loin", rg.pts, 0.12, pal.far, { ...o, snowY: 86, peaks: rg, albedo: 0.15 });
      return;
    }
    case "toundra": {
      const rg = mountainRidge(r, 130, 46, 4, 0.16);
      drawLayer(p, "loin", rg.pts, 0.15, pal.far, { ...o, snowY: 106, peaks: rg, albedo: 0.7 });
      return;
    }
    case "volcan":
      paintVolcano(p, r, st);
      return;
    case "desert": {
      if (r.chance(0.6)) drawLayer(p, "loin", mesaPts(r, 131, 36), 0.1, pal.far, { ...o, albedo: 0.1 });
      else {
        const dn = duneLayer(r, 131, 18, 3);
        drawLayerD(p, "loin", dn.top, dn.fill, 0.12, pal.far, { ...o, albedo: 0.1 });
      }
      return;
    }
    case "plaine": {
      if (r.chance(0.5)) {
        const rg = mountainRidge(r, 126, 46, 4, 0.18);
        drawLayer(p, "tresloin", rg.pts, 0, mix(pal.far, "#a3b6cf", 0.4), { ...o, snowY: 96, peaks: rg, albedo: 0.2 });
      }
      drawLayer(p, "loin", hillPts(r, 133, 20, 2.4), 0.2, pal.far, { ...o, smooth: true });
      return;
    }
    case "foret": {
      const pts = hillPts(r, 133, 26, 2.2);
      drawLayer(p, "loin", pts, 0.18, pal.far, { ...o, smooth: true, texture: fringeD(r, pts, "sapins", 4, 9, 3.6) });
      return;
    }
    case "jungle": {
      const pts = hillPts(r, 133, 34, 2.6);
      drawLayer(p, "loin", pts, 0.18, pal.far, { ...o, smooth: true, texture: fringeD(r, pts, "feuillus", 4, 8, 5) });
      return;
    }
    case "marais": {
      const pts = hillPts(r, 134, 12, 1.8);
      drawLayer(p, "loin", pts, 0.15, pal.far, { ...o, smooth: true, texture: fringeD(r, pts, "feuillus", 2, 4, 8) });
      return;
    }
    default:
      drawLayer(p, "loin", hillPts(r, 133, 20, 2), 0.15, pal.far, { ...o, smooth: true });
  }
}

function smokePuffs(r: Prng, x: number, y: number, n: number, color: string, size: number, rise: number, opacity = 0.45): El[] {
  const out: El[] = [];
  for (let i = 0; i < n; i++) {
    const dur = r.range(5, 9) * (rise > 30 ? 1.5 : 1);
    out.push(
      <circle key={i} class="scene-smoke" cx={f1(x + i * size * 0.4 + r.range(-1, 1))} cy={f1(y - i * size * 1.3)} r={f1(size * r.range(0.8, 1.2))} fill={color}
        opacity={f2(opacity * 0.7)} style={anim(dur, -r.range(0, dur), { o: f2(opacity), dx: px(r.range(4, 12)), dy: px(-rise * r.range(0.8, 1.2)) })} />,
    );
  }
  return out;
}

function paintVolcano(p: Painter, r: Prng, st: Stage): void {
  const e = p.env;
  const pal = PAL.volcan;
  const ridge = mountainRidge(r, 130, 30, 4, 0.22);
  drawLayer(p, "chaine", ridge.pts, 0.08, mix(pal.far, "#6a5a5a", 0.3), { reflect: st, peaks: ridge });
  const vx = st.sx < 200 ? r.range(270, 320) : r.range(80, 130);
  const cone: Pt[] = [[vx - 170, 134], [vx - 96, 112], [vx - 46, 82], [vx - 14, 55], [vx - 6, 60], [vx + 5, 59], [vx + 13, 54], [vx + 48, 84], [vx + 100, 113], [vx + 175, 134]];
  const pts: Pt[] = [];
  for (let i = 0; i < cone.length; i++) {
    pts.push(cone[i]);
    if (i < cone.length - 1 && (i < 3 || i > 5)) {
      const [ax, ay] = cone[i];
      const [bx, by] = cone[i + 1];
      pts.push([(ax + bx) / 2 + r.range(-3, 3), (ay + by) / 2 + r.range(-3, 3)]);
    }
  }
  const top = polyD(pts, false);
  drawLayerD(p, "volcan", top, `${top}L${pt(vx + 175, 215)}L${pt(vx - 170, 215)}Z`, 0.2, pal.far, { reflect: st });
  p.add(<ellipse key={p.key()} class="scene-glow-pulse" cx={f1(vx)} cy="50" rx="50" ry="30" fill={p.glow("#ff4d1f", e.night ? 0.6 : 0.38)} style={anim(3.2, 0)} />);
  let flows = "";
  for (let i = 0; i < 3; i++) {
    const dir = i === 1 ? 0.25 : i ? 1 : -1;
    let x = vx + dir * 7;
    let y = 58;
    flows += `M${pt(x, y)}`;
    for (let k = 0; k < 5; k++) {
      x += dir * r.range(6, 13) + r.range(-3, 3);
      y += r.range(9, 14);
      flows += `L${pt(x, y)}`;
    }
  }
  p.add(
    <g key={p.key()}>
      <path class="scene-glow-pulse" d={flows} fill="none" stroke="#ff5a1a" stroke-width="3.4" opacity="0.35" stroke-linejoin="round" style={anim(2.6, -1)} />
      <path d={flows} fill="none" stroke="#ffb347" stroke-width="0.9" stroke-linejoin="round" />
    </g>,
  );
  p.add(<g key={p.key()}>{smokePuffs(r, vx, 50, 7, e.night ? "#2a2020" : "#4a4040", 6.5, 36, 0.55)}</g>);
  st.embers.push({ x: vx, y: 58, spread: 10, count: 8, rise: 50, colors: ["#ffb347", "#ff6a1f"] });
}

function paintWaves(p: Painter, y0: number, count: number): void {
  const e = p.env;
  const r = rngFor("vagues", e.seed, y0);
  const color = e.night ? "#8fa7d6" : "#eaf6fb";
  let still = "";
  const moving: El[] = [];
  for (let i = 0; i < count; i++) {
    const t = i / count;
    const y = y0 + 2 + (200 - y0) * Math.pow(t, 1.6);
    const len = 4 + 22 * t;
    const x = r.range(-10, 410);
    const d = `M${pt(x, y)}q${f1(len / 2)} ${f1(-0.8 - t)} ${f1(len)} 0`;
    if (r.chance(0.35)) {
      moving.push(
        <path key={i} class="scene-shimmer" d={d} fill="none" stroke={color} stroke-width={f2(0.4 + 0.8 * t)} stroke-linecap="round"
          opacity={f2(0.15 + 0.3 * t)} style={anim(r.range(2.5, 5), -r.range(0, 4), { o: f2(0.25 + 0.35 * t) })} />,
      );
    } else still += d;
  }
  p.add(<g key={p.key()}><path d={still} fill="none" stroke={color} stroke-width="0.7" opacity="0.26" stroke-linecap="round" />{moving}</g>);
}

/** Traînée de reflets sous le soleil ou la lune. */
function paintGlitter(p: Painter, y0: number): void {
  const e = p.env;
  const src = e.night ? e.moonAt : e.sun;
  if (!src || e.overcast || (!e.night && src[1] > y0)) return;
  const r = rngFor("reflet", e.seed);
  const color = e.night ? "#e8ecff" : e.phase === "jour" ? "#fff6dc" : "#ffc58a";
  const strength = e.night ? 0.2 + 0.55 * ((1 - Math.cos(2 * Math.PI * e.moon)) / 2) : 0.8;
  const els: El[] = [];
  for (let i = 0; i < 14; i++) {
    const t = i / 14;
    const y = y0 + 1.5 + (190 - y0) * Math.pow(t, 1.3);
    const w = 3 + 16 * t * r.range(0.6, 1.2);
    const x = src[0] + r.range(-4, 4) * (1 + t * 3);
    els.push(
      <path key={i} class="scene-shimmer" d={`M${pt(x - w / 2, y)}h${f1(w)}`} stroke={color} stroke-width={f2(0.6 + t)} stroke-linecap="round"
        opacity={f2(strength * (0.8 - 0.5 * t))} style={anim(r.range(1.6, 3.2), -r.range(0, 3), { o: f2(strength * (0.9 - 0.5 * t)) })} />,
    );
  }
  p.add(<g key={p.key()}>{els}</g>);
}

function paintSea(p: Painter, st: Stage): void {
  const e = p.env;
  const y0 = st.waterY;
  const top = mix(e.sky[3], "#5d97b3", e.night ? 0.2 : 0.3);
  const deep = p.tone("#164a66", 0.85);
  const fill = p.def("mer", (id) => (
    <linearGradient id={id} gradientUnits="userSpaceOnUse" x1="0" y1={f1(y0)} x2="0" y2="200">
      <stop offset="0" stop-color={top} />
      <stop offset="0.35" stop-color={mix(top, deep, 0.55)} />
      <stop offset="1" stop-color={deep} />
    </linearGradient>
  ));
  p.add(<rect key={p.key()} x="-20" y={f1(y0)} width="440" height={f1(220 - y0)} fill={fill} />);
  p.add(<path key={p.key()} d={`M-20 ${f1(y0 + 0.3)}H420`} stroke={mix(top, "#ffffff", 0.35)} stroke-width="0.6" opacity="0.6" />);
  paintWaves(p, y0, 30);
  paintGlitter(p, y0);
}

function paintIslands(p: Painter, r: Prng, st: Stage): void {
  let d = "";
  const n = r.int(1, 2);
  for (let i = 0; i < n; i++) {
    const cx = r.range(30, 370);
    const w = r.range(40, 110);
    const h = r.range(4, 11);
    const pts: Pt[] = [];
    for (let k = 0; k <= 12; k++) {
      const t = k / 12;
      pts.push([cx - w / 2 + w * t, st.waterY + 0.6 - h * Math.pow(Math.sin(Math.PI * t), 0.7) * r.range(0.8, 1.1)]);
    }
    d += `${smoothD(pts)}Z`;
  }
  p.add(<path key={p.key()} d={d} fill={p.tone(PAL.cote.far, 0.08)} />);
}

function paintCliff(p: Painter, r: Prng, st: Stage): void {
  const left = st.sx > 200;
  const w = r.range(90, 130);
  const topY = r.range(98, 112);
  const xs = left ? -30 : 430;
  const dir = left ? 1 : -1;
  const edgeX = xs + dir * w;
  const top: Pt[] = [];
  for (let i = 0; i <= 10; i++) {
    const t = i / 10;
    top.push([xs + (edgeX - xs) * t, topY + r.range(-4, 4) + t * t * 8]);
  }
  const foot = st.waterY + 34;
  st.cliffTop = top[5];
  const face: Pt[] = [[edgeX + dir * 6, topY + 22], [edgeX + dir * 14, foot - 10], [edgeX + dir * 22, foot], [edgeX + dir * 22, 215], [xs, 215]];
  p.add(<path key={p.key()} d={polyD([...top, ...face])} fill={p.tone(PAL.cote.mid, 0.5)} />);
  const lip: Pt[] = top.slice().reverse().map(([x, y]): Pt => [x, y + 4]);
  p.add(<path key={p.key()} d={polyD([...top, ...lip])} fill={p.tone("#5f8a45", 0.5)} />);
  let stri = "";
  for (let i = 0; i < 8; i++) {
    const x = edgeX - dir * r.range(4, 70);
    stri += `M${pt(x, topY + r.range(9, 20))}l${f1(r.range(-2, 2))} ${f1(r.range(12, 30))}`;
  }
  p.add(<path key={p.key()} d={stri} stroke={p.tone("#4f4a40", 0.55)} stroke-width="0.7" opacity="0.6" fill="none" />);
  const fx = edgeX + dir * 22;
  p.add(<path key={p.key()} class="scene-shimmer" d={`M${pt(fx - 26, foot + 1)}q6 -2.5 12 0t12 0t12 0t12 0`} fill="none" stroke="#f2fbff" stroke-width="1.1" opacity="0.6" style={anim(2.8, 0, { o: "0.85" })} />);
}

function paintLake(p: Painter, st: Stage): void {
  const e = p.env;
  const y0 = st.waterY;
  const deep = p.tone(e.biome === "marais" ? "#2a4436" : e.under ? "#1d3350" : "#1f4a63", 0.7);
  const fill = p.def("lac", (id) => (
    <linearGradient id={id} gradientUnits="userSpaceOnUse" x1="0" y1={f1(y0)} x2="0" y2="200">
      <stop offset="0" stop-color={mix(e.sky[3], deep, 0.25)} />
      <stop offset="0.5" stop-color={mix(e.sky[1], deep, 0.45)} />
      <stop offset="1" stop-color={mix(e.sky[0], deep, 0.6)} />
    </linearGradient>
  ));
  const h = f1(220 - y0);
  p.add(<rect key={p.key()} x="-20" y={f1(y0)} width="440" height={h} fill={fill} />);
  const clip = p.def("eau", (id) => <clipPath id={id}><rect x="-20" y={f1(y0)} width="440" height={h} /></clipPath>);
  p.add(
    <g key={p.key()} clip-path={clip} opacity="0.62">
      <g transform={`matrix(1 0 0 -1 0 ${f1(2 * y0)})`}>{st.reflect.map((l, i) => <path key={i} d={l.d} fill={l.fill} />)}</g>
    </g>,
  );
  const veil = p.def("voile-eau", (id) => (
    <linearGradient id={id} gradientUnits="userSpaceOnUse" x1="0" y1={f1(y0)} x2="0" y2="200">
      <stop offset="0" stop-color={deep} stop-opacity="0.05" />
      <stop offset="1" stop-color={deep} stop-opacity="0.45" />
    </linearGradient>
  ));
  p.add(<rect key={p.key()} x="-20" y={f1(y0)} width="440" height={h} fill={veil} />);
  p.add(<path key={p.key()} d={`M-20 ${f1(y0 + 0.4)}H420`} stroke={mix(e.sky[3], "#ffffff", 0.3)} stroke-width="0.5" opacity="0.45" />);
  paintWaves(p, y0, 16);
  paintGlitter(p, y0);
}

function crystalCluster(p: Painter, r: Prng, x: number, base: number, size: number, color: string, key: number): El {
  let d = "";
  let hi = "";
  const n = r.int(3, 5);
  for (let i = 0; i < n; i++) {
    const cx = x + r.range(-0.5, 0.5) * size;
    const h = size * r.range(0.6, 1.5);
    const w = size * r.range(0.22, 0.34);
    const lean = r.range(-0.6, 0.6) * w;
    d += polyD([[cx - w / 2, base], [cx - w / 2 + lean * 0.7, base - h * 0.78], [cx + lean, base - h], [cx + w / 2 + lean * 0.7, base - h * 0.78], [cx + w / 2, base]]);
    hi += polyD([[cx, base], [cx + lean, base - h], [cx + w / 2 + lean * 0.7, base - h * 0.78], [cx + w / 2, base]]);
  }
  const dur = r.range(2.5, 4.5);
  return (
    <g key={key}>
      <circle class="scene-glow-pulse" cx={f1(x)} cy={f1(base - size * 0.6)} r={f1(size * 2.6)} fill={p.glow(color, 0.5)} style={anim(dur, -r.range(0, dur))} />
      <path d={d} fill={mix(color, "#1a1030", 0.4)} />
      <path d={hi} fill={color} opacity="0.6" />
    </g>
  );
}

function paintCaveBack(p: Painter, r: Prng, st: Stage): void {
  const e = p.env;
  const pal = PAL.souterrain;
  const glows: El[] = [];
  for (let i = 0; i < 5; i++) glows.push(<circle key={i} cx={f1(r.range(10, 390))} cy={f1(r.range(40, 120))} r={f1(r.range(28, 60))} fill={p.glow(r.pick(CRYSTALS), 0.14)} />);
  p.add(<g key={p.key()}>{glows}</g>);
  if (!e.night) {
    const x0 = r.range(70, 330);
    const shaft = p.def("puits", (id) => (
      <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="#fff4d6" stop-opacity={e.phase === "jour" ? "0.42" : "0.22"} />
        <stop offset="1" stop-color="#fff4d6" stop-opacity="0" />
      </linearGradient>
    ));
    p.add(<path key={p.key()} class="scene-rays" d={polyD([[x0 - 7, -2], [x0 + 7, -2], [x0 + 46, 178], [x0 - 20, 178]])} fill={shaft} style={anim(7, 0)} />);
    let dust = "";
    for (let i = 0; i < 18; i++) {
      const t = r.next();
      dust += circleD(x0 + t * 13 + r.range(-6, 6) * (0.3 + t), 4 + t * 170, r.range(0.25, 0.5));
    }
    p.add(<path key={p.key()} d={dust} fill="#fff4d6" opacity="0.5" />);
  }
  let pillars = "";
  for (let i = 0; i < 2; i++) {
    const x = r.chance(0.5) ? r.range(20, Math.max(30, st.sx - 70)) : r.range(Math.min(370, st.sx + 70), 380);
    const w = r.range(10, 16);
    pillars += `M${pt(x - w, 215)}Q${pt(x - w * 0.25, 100)} ${pt(x - w * 0.9, -5)}L${pt(x + w * 0.9, -5)}Q${pt(x + w * 0.25, 100)} ${pt(x + w, 215)}Z`;
  }
  const spikes: Pt[] = [[-30, 136]];
  for (let x = -30; x < 430;) {
    const w = r.range(6, 16);
    const h = r.range(6, 32);
    spikes.push([x + w * 0.1, 136 + r.range(-1, 2)], [x + w * 0.42, 136 - h], [x + w * 0.58, 136 - h + r.range(0, 2)], [x + w * 0.9, 136]);
    x += w + r.range(0, 10);
  }
  spikes.push([440, 136]);
  const top = polyD(spikes, false);
  drawLayerD(p, "grotte-loin", top, closeDown(top) + pillars, 0.2, pal.far, { reflect: st, rim: false });
  if (st.water === "lac") {
    for (const x of [r.range(30, 120), r.range(280, 370)]) p.add(crystalCluster(p, r, x, st.waterY + 1, r.range(8, 12), r.pick(CRYSTALS), p.key()));
    return;
  }
  const pts = hillPts(r, 156, 16, 3, 6).map(([x, y]): Pt => [x, y + r.range(-2, 2)]);
  drawLayer(p, "grotte-milieu", pts, 0.5, pal.mid, { rim: false });
  for (let i = 0; i < 3; i++) {
    const x = r.range(20, 380);
    if (Math.abs(x - st.sx) < 50) continue;
    p.add(crystalCluster(p, r, x, yAt(pts, x) + 3, r.range(7, 12), r.pick(CRYSTALS), p.key()));
  }
}

function paintMid(p: Painter, r: Prng, st: Stage): void {
  const e = p.env;
  const pal = PAL[e.biome];
  if (st.water === "lac") {
    const pts = hillPts(r, st.waterY + 1.5, 10, 2.2);
    const fringe = e.biome === "desert" ? "" : fringeD(r, pts, TREES[e.biome][0] === "sapin" ? "sapins" : "feuillus", 3, 7, 5);
    drawLayer(p, "rive", pts, 0.5, pal.mid, { smooth: true, albedo: pal.albedo, reflect: st, texture: fringe });
    return;
  }
  if (st.water === "mer") {
    paintCliff(p, r, st);
    if (e.kind !== "port" && e.kind !== "lac") drawLayer(p, "littoral", hillPts(r, 155, 10, 2), 0.55, "#7d9a5c", { smooth: true });
    return;
  }
  if (e.kind === "col") {
    const pts: Pt[] = [];
    for (let x = -30; x <= 430; x += 6) {
      const d = Math.max(0, Math.abs(x - st.sx) - 14) / 170;
      pts.push([x, 152 - 58 * Math.pow(Math.min(1, d), 0.75) + r.range(-2.5, 2.5)]);
    }
    drawLayer(p, "flancs", pts, 0.5, mix(pal.mid, PAL.montagne.mid, 0.5), { albedo: Math.max(0.15, pal.albedo), snowY: hasSnow(e.biome) ? 112 : undefined });
    treeRow(p, r, pts, { depth: 0.55, hmin: 7, hmax: 12, step: 7, kinds: ["sapin"], skip: (x) => Math.abs(x - st.sx) < 40 || yAt(pts, x) < 114 });
    return;
  }
  switch (e.biome) {
    case "montagne": {
      const rg = mountainRidge(r, 152, 40, 3, 0.24);
      drawLayer(p, "milieu", rg.pts, 0.45, pal.mid, { peaks: rg, snowY: 118, albedo: 0.15 });
      treeRow(p, r, null, { y: 151, depth: 0.55, hmin: 8, hmax: 14, step: 8, kinds: ["sapin"] });
      return;
    }
    case "toundra": {
      const pts = hillPts(r, 152, 14, 2);
      drawLayer(p, "milieu", pts, 0.5, pal.mid, { smooth: true, albedo: 0.7 });
      treeRow(p, r, pts, { depth: 0.55, hmin: 8, hmax: 14, step: 26, kinds: ["sapin", "sapin", "mort"] });
      return;
    }
    case "volcan": {
      const rg = mountainRidge(r, 152, 24, 4, 0.32);
      drawLayer(p, "milieu", rg.pts, 0.5, pal.mid, { peaks: rg });
      let river = `M-20 ${f1(r.range(150, 156))}`;
      for (let x = 20; x <= 440; x += 40) river += `Q${pt(x - 20, r.range(146, 160))} ${pt(x, r.range(149, 157))}`;
      p.add(
        <g key={p.key()}>
          <path class="scene-glow-pulse" d={river} fill="none" stroke="#ff5a1a" stroke-width="5" opacity="0.3" style={anim(3, -1.2)} />
          <path d={river} fill="none" stroke="#ffa040" stroke-width="1.3" opacity="0.9" />
        </g>,
      );
      treeRow(p, r, rg.pts, { depth: 0.6, hmin: 8, hmax: 14, step: 40, kinds: ["mort"] });
      return;
    }
    case "desert": {
      const dn = duneLayer(r, 156, 20, 3);
      drawLayerD(p, "milieu", dn.top, dn.fill, 0.5, pal.mid, { albedo: 0.1 });
      p.add(<path key={p.key()} d={dn.shade} fill={p.tone("#8a5a32", 0.6)} opacity="0.3" />);
      return;
    }
    case "marais": {
      const pts = hillPts(r, 151, 5, 3);
      drawLayer(p, "milieu", pts, 0.45, pal.mid, { smooth: true });
      treeRow(p, r, pts, { depth: 0.55, hmin: 14, hmax: 24, step: 34, kinds: ["saule", "mort"] });
      return;
    }
    case "jungle": {
      const pts = hillPts(r, 150, 14, 2);
      drawLayer(p, "milieu", pts, 0.45, pal.mid, { smooth: true, texture: fringeD(r, pts, "feuillus", 6, 12, 6) });
      if (e.kind !== "foret" && e.kind !== "clairiere") treeRow(p, r, pts, { depth: 0.55, hmin: 16, hmax: 26, step: 22, kinds: ["palmier", "tropical"] });
      return;
    }
    case "foret": {
      const pts = hillPts(r, 151, 12, 2);
      drawLayer(p, "milieu", pts, 0.45, pal.mid, { smooth: true, texture: fringeD(r, pts, "sapins", 6, 11, 4) });
      return;
    }
    default: {
      const pts = hillPts(r, 152, 16, 2.2);
      drawLayer(p, "milieu", pts, 0.5, pal.mid, { smooth: true });
      treeRow(p, r, pts, { depth: 0.55, hmin: 10, hmax: 16, step: 60, kinds: TREES[e.biome] });
    }
  }
}

function paintTerrain(p: Painter): Stage {
  const e = p.env;
  const r = rngFor("relief", e.seed, e.biome, e.kind);
  const water: WaterMode = e.biome === "cote" ? "mer" : e.kind === "lac" || e.kind === "port" ? "lac" : null;
  const st: Stage = {
    sx: 200 + r.range(-30, 30), base: 160, water, waterY: water === "mer" ? 121 : 141,
    reflect: [], embers: [], motes: [], mist: false, bats: false, saddleY: 112, cliffTop: null,
  };
  if (e.under) {
    paintCaveBack(p, r, st);
    if (water === "lac") paintLake(p, st);
    return st;
  }
  if (water === "mer") {
    paintSea(p, st);
    paintIslands(p, r, st);
  } else paintFar(p, r, st);
  if (e.weather === "brume") {
    const fill = p.glow(mix("#ffffff", e.fog, 0.4), 0.5);
    p.add(
      <g key={p.key()}>
        <ellipse cx={f1(r.range(80, 160))} cy="132" rx="190" ry="14" fill={fill} />
        <ellipse cx={f1(r.range(240, 320))} cy="138" rx="170" ry="11" fill={fill} />
      </g>,
    );
  }
  paintMid(p, r, st);
  if (water === "lac") paintLake(p, st);
  return st;
}

// ───────── Sujets : habitations ─────────

interface HouseStyle { roof: "pointu" | "plat" | "chaume"; stilts: boolean; snow: boolean; walls: string[]; roofs: string[] }
interface HouseColors { wall: string; wallShade: string; roof: string; dark: string; snow: string; wood: string; trim: string }
interface House { x: number; base: number; w: number; h: number; tint: number; chimney: boolean; side: boolean; back: boolean; dome: boolean }

function houseStyle(b: Biome, weather: SceneWeather): HouseStyle {
  let s: HouseStyle;
  switch (b) {
    case "montagne":
    case "toundra":
      s = { roof: "pointu", stilts: false, snow: true, walls: ["#8d6c4c", "#7b5d42", "#9b7b58"], roofs: ["#4a3b31", "#3f3a3c"] };
      break;
    case "desert":
      s = { roof: "plat", stilts: false, snow: false, walls: ["#e9cb9b", "#ddb683", "#f0d8ab"], roofs: ["#c99a63", "#d4a66f"] };
      break;
    case "cote":
      s = { roof: "pointu", stilts: false, snow: false, walls: ["#efe8dc", "#e5dccd", "#dbe3e8"], roofs: ["#b4533c", "#3f6e8e", "#a5452f"] };
      break;
    case "marais":
    case "jungle":
      s = { roof: "chaume", stilts: true, snow: false, walls: ["#8d7754", "#7c6747"], roofs: ["#a38c56", "#917b49"] };
      break;
    case "souterrain":
      s = { roof: "plat", stilts: false, snow: false, walls: ["#6d667c", "#5e586e"], roofs: ["#4c4660", "#544d68"] };
      break;
    case "volcan":
      s = { roof: "pointu", stilts: false, snow: false, walls: ["#4d4242", "#3d3636"], roofs: ["#2b2525", "#332a28"] };
      break;
    default:
      s = { roof: "pointu", stilts: false, snow: false, walls: ["#dac5a1", "#cbb088", "#bb9d77"], roofs: ["#7b402f", "#6b4b37", "#5d4138"] };
  }
  if (weather === "neige") s.snow = true;
  return s;
}

function houseColors(p: Painter, style: HouseStyle, tint: number, depth: number): HouseColors {
  const w = style.walls[tint % style.walls.length];
  return {
    wall: p.tone(w, depth),
    wallShade: p.tone(mix(w, "#000000", 0.3), depth),
    roof: p.tone(style.roofs[tint % style.roofs.length], depth),
    dark: p.tone("#23252e", depth),
    snow: p.tone("#f4f7fb", depth, 1),
    wood: p.tone("#5a4130", depth),
    trim: p.tone("#e9dcbc", depth),
  };
}

function addHouse(b: Batch, r: Prng, h: House, style: HouseStyle, c: HouseColors, e: Env, windows: Pt[], chimneys: Pt[]): void {
  const lift = style.stilts ? 5 : 0;
  const bottom = h.base - lift;
  const top = bottom - h.h;
  const left = h.x - h.w / 2;
  const right = h.x + h.w / 2;
  if (lift) {
    let posts = "";
    for (const x of [left + 1.5, h.x, right - 1.5]) posts += rectD(x - 0.6, bottom, 1.2, lift + 1);
    b.fill(c.wood, posts);
  }
  b.fill(c.wall, rectD(left, top, h.w, h.h));
  b.fill(c.wallShade, e.lightFromLeft ? rectD(right - h.w * 0.28, top, h.w * 0.28, h.h) : rectD(left, top, h.w * 0.28, h.h), 0.55);
  const ov = 2;
  if (style.roof === "pointu") {
    const rh = h.w * (h.side ? 0.4 : 0.62);
    if (h.side) {
      const roof: Pt[] = [[left - ov, top], [left + rh * 0.7, top - rh], [right - rh * 0.7, top - rh], [right + ov, top]];
      b.fill(c.roof, polyD(roof));
      if (style.snow) b.fill(c.snow, polyD([...roof, [right + ov - 2.6, top], [right - rh * 0.7 - 0.4, top - rh + 2.2], [left + rh * 0.7 + 0.4, top - rh + 2.2], [left - ov + 2.6, top]]));
    } else {
      b.fill(c.roof, polyD([[left - ov, top], [h.x, top - rh], [right + ov, top]]));
      if (style.snow) b.fill(c.snow, polyD([[left - ov, top], [h.x, top - rh], [right + ov, top], [right + ov - 2.6, top], [h.x, top - rh + 2.4], [left - ov + 2.6, top]]));
    }
    if (h.chimney) {
      const cx = h.x + h.w * (h.side ? 0.25 : 0.2) * (r.chance(0.5) ? 1 : -1);
      const roofY = h.side ? top - rh : top - rh * (1 - Math.abs(cx - h.x) / (h.w / 2 + ov));
      b.fill(c.roof, rectD(cx - 1.6, roofY - 4.5, 3.2, 6.5));
      chimneys.push([cx, roofY - 5.5]);
    }
  } else if (style.roof === "chaume") {
    b.fill(c.roof, `M${pt(left - 3, top + 1)}Q${pt(h.x, top - h.w * 0.8)} ${pt(right + 3, top + 1)}Z`);
  } else {
    b.fill(c.roof, rectD(left - 1, top - 2, h.w + 2, 2.2));
    if (h.dome) b.fill(c.roof, `M${pt(h.x - h.w * 0.3, top - 1.5)}a${f1(h.w * 0.3)} ${f1(h.w * 0.3)} 0 0 1 ${f1(h.w * 0.6)} 0Z`);
    else if (h.chimney) chimneys.push([left + h.w * 0.2, top - 3]);
  }
  const nw = h.w > 17 ? 2 : 1;
  const wy = top + h.h * 0.3;
  for (let k = 0; k < nw; k++) {
    const wx = nw === 1 ? h.x + h.w * 0.2 : h.x + (k ? 1 : -1) * h.w * 0.28;
    const lit = e.lamps && r.chance(0.78);
    b.fill(lit ? LAMP : c.dark, style.roof === "plat" ? archD(wx, wy + 3.4, 2.6, 3.4) : rectD(wx - 1.3, wy, 2.6, 3.2));
    if (lit) windows.push([wx, wy + 1.6]);
  }
  b.fill(c.dark, archD(nw === 1 ? h.x - h.w * 0.2 : h.x, bottom, 3.6, 6));
}

function addBellTower(b: Batch, x: number, base: number, c: HouseColors, e: Env, windows: Pt[]): void {
  const w = 12;
  const top = base - 52;
  b.fill(c.wall, rectD(x - w / 2, top, w, 52));
  b.fill(c.wallShade, e.lightFromLeft ? rectD(x + w * 0.2, top, w * 0.3, 52) : rectD(x - w / 2, top, w * 0.3, 52), 0.55);
  b.fill(c.roof, polyD([[x - w / 2 - 2, top + 0.5], [x, top - 32], [x + w / 2 + 2, top + 0.5]]) + rectD(x - w / 2 - 1.5, top - 1, w + 3, 2.4));
  b.fill(e.lamps ? LAMP : c.dark, archD(x, top + 14, 5, 9));
  if (e.lamps) windows.push([x, top + 10]);
  b.fill(c.trim, circleD(x, top + 22, 2.4));
  b.fill(c.dark, rectD(x - 0.6, top + 30, 1.2, 5));
  b.fill("#d9b45a", circleD(x, top - 33, 1.3));
}

/** Butte de terrain sous le sujet, au ton du sujet (il ne flotte jamais). */
function groundUnder(p: Painter, st: Stage, x0: number, x1: number, depth = 0.74): void {
  const e = p.env;
  const pal = PAL[e.biome];
  const base = e.biome === "cote" ? "#7d9a5c" : e.under ? pal.mid : mix(pal.mid, pal.near, 0.5);
  const y = st.base - 2;
  p.add(<path key={p.key()} d={`M${pt(x0 - 44, 186)}C${pt(x0 - 20, y + 3)} ${pt(x0 - 8, y)} ${pt(x0 + 8, y)}L${pt(x1 - 8, y)}C${pt(x1 + 8, y)} ${pt(x1 + 20, y + 3)} ${pt(x1 + 44, 186)}Z`} fill={p.tone(base, depth, pal.albedo)} />);
}

function paintSettlement(p: Painter, st: Stage): void {
  const e = p.env;
  const r = rngFor("bourg", e.seed, e.kind, e.biome);
  const big = e.kind === "ville";
  const tiny = e.kind === "hameau";
  const style = houseStyle(e.biome, e.weather);
  const n = tiny ? r.int(3, 4) : big ? r.int(9, 11) : r.int(5, 7);
  const spread = tiny ? 120 : big ? 260 : 200;
  groundUnder(p, st, st.sx - spread / 2, st.sx + spread / 2);
  const trees = new Batch();
  const tc = treeColors(p, 0.7);
  for (let i = 0; i < (tiny ? 2 : 3); i++) addTree(trees, r, r.pick(TREES[e.biome]), st.sx + r.range(-spread / 2 - 12, spread / 2 + 12), st.base - 5, r.range(16, 26), tc, e.lightFromLeft);
  p.batch(trees);
  const houses: House[] = [];
  for (let i = 0; i < n; i++) {
    const back = big ? i % 2 === 1 : r.chance(0.3);
    const s = (back ? 0.8 : 1) * (big ? 1.05 : tiny ? 0.92 : 1);
    houses.push({
      x: st.sx - spread / 2 + (spread * (i + r.range(0.25, 0.75))) / n,
      base: st.base - (back ? r.range(5, 8) : r.range(-1, 1.5)),
      w: r.range(14, 21) * s,
      h: r.range(10, 14) * s * (big ? 1.3 : 1),
      tint: r.int(0, 5),
      chimney: style.roof !== "chaume" && r.chance(0.6),
      side: r.chance(0.4),
      back,
      dome: style.roof === "plat" && r.chance(0.3),
    });
  }
  const windows: Pt[] = [];
  const chimneys: Pt[] = [];
  const draw = (list: House[]): void => {
    for (const h of list) {
      const b = new Batch();
      addHouse(b, r, h, style, houseColors(p, style, h.tint, h.back ? 0.72 : 0.84), e, windows, chimneys);
      p.batch(b);
    }
  };
  draw(houses.filter((h) => h.back));
  if (big) {
    const b = new Batch();
    addBellTower(b, st.sx + r.range(-40, 40), st.base - 4, houseColors(p, style, 0, 0.78), e, windows);
    p.batch(b);
  }
  draw(houses.filter((h) => !h.back));
  if (big) {
    const stone = p.tone(e.biome === "desert" ? "#d9b886" : "#a9a293", 0.88);
    const shade = p.tone("#6f6a60", 0.9);
    const x0 = st.sx - spread / 2 - 10;
    const x1 = st.sx + spread / 2 + 10;
    const wt = st.base - 7;
    const b = new Batch();
    b.fill(stone, rectD(x0, wt, x1 - x0, 14) + crenelsD(x0, x1, wt));
    b.fill(p.tone("#15161c", 0.9), archD(st.sx + r.range(-30, 30), wt + 14, 9, 12));
    for (const tx of [x0, x1]) {
      b.fill(stone, rectD(tx - 6, wt - 9, 12, 24) + crenelsD(tx - 6.5, tx + 6.5, wt - 9, 2.6, 2.6, 2.1));
      b.fill(shade, rectD(e.lightFromLeft ? tx + 1.5 : tx - 6, wt - 9, 4.5, 24), 0.5);
    }
    p.batch(b);
  }
  windowGlows(p, r, windows);
  const smoke = e.night ? "#59617a" : e.phase === "jour" ? "#e4e7ea" : "#b9a9b5";
  const puffs = chimneys.slice(0, 5).map(([x, y], i) => <g key={i}>{smokePuffs(r, x, y, 3, smoke, 2.2, 22, e.night ? 0.26 : 0.38)}</g>);
  if (puffs.length) p.add(<g key={p.key()}>{puffs}</g>);
}

// ───────── Sujets : nature et pierres ─────────

function torch(p: Painter, r: Prng, st: Stage, x: number, y: number, s: number, pole: number): El {
  const d = r.range(0.7, 1.1);
  st.embers.push({ x, y: y - 6 * s, spread: 2, count: 2, rise: 18, colors: ["#ffd27a", "#ff9a3d"] });
  return (
    <g key={p.key()}>
      <circle class="scene-flicker" cx={f1(x)} cy={f1(y - 3 * s)} r={f1(18 * s)} fill={p.glow("#ff9a3d", 0.55)} style={anim(r.range(1.8, 2.8), -r.range(0, 2))} />
      <path d={`M${pt(x, y + pole)}V${f1(y)}`} stroke={p.tone("#4a3626", 0.85)} stroke-width={f2(1.5 * s)} stroke-linecap="round" />
      <path d={`M${pt(x - 1.6 * s, y)}h${f1(3.2 * s)}`} stroke={p.tone("#2a2a30", 0.85)} stroke-width={f2(1.4 * s)} stroke-linecap="round" />
      <path class="scene-flame" d={flameD(x, y - 0.4 * s, 2.6 * s, 7 * s)} fill="#ff7a22" style={anim(d, -r.range(0, 1))} />
      <path class="scene-flame" d={flameD(x, y - 0.4 * s, 1.4 * s, 4.4 * s)} fill="#ffe08a" style={anim(d * 0.8, -r.range(0, 1))} />
    </g>
  );
}

/** Rayons de lumière obliques (sous-bois, clairières). */
function lightRays(p: Painter, r: Prng, count: number, aim?: number): void {
  const e = p.env;
  if (e.night || e.overcast || e.under) return;
  const color = e.phase === "jour" ? "#fff6d8" : "#ffc58a";
  const fill = p.def("rayons", (id) => (
    <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color={color} stop-opacity="0.34" />
      <stop offset="1" stop-color={color} stop-opacity="0" />
    </linearGradient>
  ));
  const dir = e.lightFromLeft ? 1 : -1;
  const els: El[] = [];
  for (let i = 0; i < count; i++) {
    const w = r.range(8, 18);
    const x = aim === undefined ? r.range(60, 340) : aim - dir * 70 + r.range(-30, 22);
    els.push(<path key={i} class="scene-rays" d={polyD([[x, -4], [x + w, -4], [x + w + dir * 70, 176], [x + dir * 70 - w * 0.4, 176]])} fill={fill} style={anim(r.range(5, 9), -r.range(0, 6))} />);
  }
  p.add(<g key={p.key()}>{els}</g>);
}

function paintForestKind(p: Painter, st: Stage, clearing: boolean): void {
  const e = p.env;
  const r = rngFor("bois", e.seed, e.kind, e.biome);
  const kinds = TREES[e.biome];
  const sparse = kinds.includes("palmier") || kinds.includes("cactus") ? 2.2 : 1;
  const gap = (x: number, w: number): boolean => clearing && Math.abs(x - st.sx) < w;
  treeRow(p, r, null, { y: 150, depth: 0.55, hmin: 18, hmax: 28, step: 6 * sparse, kinds, skip: (x) => gap(x, 56) });
  lightRays(p, r, clearing ? 3 : 2, clearing ? st.sx : undefined);
  treeRow(p, r, null, { y: 158, depth: 0.7, hmin: 26, hmax: 40, step: 9 * sparse, kinds, skip: (x) => gap(x, 72) });
  if (clearing) {
    // la clairière : prairie éclairée, menhir gravé d'une rune, souche
    groundUnder(p, st, st.sx - 56, st.sx + 56, 0.7);
    const lightC = e.night ? "#c9d8ff" : e.phase === "jour" ? "#fff3c4" : "#ffc58a";
    p.add(<ellipse key={p.key()} cx={f1(st.sx)} cy="162" rx="74" ry="12" fill={p.glow(lightC, e.night ? 0.38 : 0.55)} />);
    const sx = st.sx + r.range(-12, 12);
    const stone = PAL[e.biome].rock;
    p.add(
      <g key={p.key()}>
        <path d={`M${pt(sx - 7, 164)}Q${pt(sx - 9, 144)} ${pt(sx - 2, 133)}Q${pt(sx + 7, 138)} ${pt(sx + 7, 164)}Z`} fill={p.tone(stone, 0.8)} />
        <path d={`M${pt(e.lightFromLeft ? sx + 1 : sx - 7, 164)}Q${pt(e.lightFromLeft ? sx + 2 : sx - 9, 146)} ${pt(sx - 2, 133)}Q${pt(sx + 7, 138)} ${pt(sx + 7, 164)}Z`} fill={p.env.shade} opacity="0.25" />
      </g>,
    );
    if (e.lamps) {
      p.add(
        <g key={p.key()}>
          <circle class="scene-glow-pulse" cx={f1(sx)} cy="148" r="13" fill={p.glow("#7cf3ff", 0.55)} style={anim(3.5, 0)} />
          <path d={`M${pt(sx - 2, 144)}l2 3.6l2 -3.6M${pt(sx, 147.6)}v5M${pt(sx - 1.8, 150)}h3.6`} stroke="#d4fbff" stroke-width="0.7" fill="none" stroke-linecap="round" />
        </g>,
      );
    }
    const tx = sx + (r.chance(0.5) ? 36 : -36);
    p.add(<path key={p.key()} d={`${rectD(tx - 5, 157, 10, 7)}${ellipseD(tx, 157, 5, 1.6)}`} fill={p.tone("#5a4130", 0.78)} />);
    if (!e.night) {
      const colors = ["#f2d36b", "#e98aa0", "#ffffff"];
      let dots = "";
      for (let i = 0; i < 26; i++) dots += circleD(st.sx + r.range(-56, 56), r.range(160, 168), r.range(0.6, 1));
      p.add(<path key={p.key()} d={dots} fill={p.tone(r.pick(colors), 0.8)} />);
    }
  }
  const framed = kinds.some((k) => k === "sapin" || k === "feuillu" || k === "tropical" || k === "saule" || k === "pin");
  if (framed) {
    const dark = p.tone(PAL[e.biome].tree, 1);
    const bark = p.tone(e.biome === "volcan" ? "#1c1414" : "#2e231b", 1);
    let trunks = "";
    let leaves = "";
    for (const side of [-1, 1]) {
      const x = side < 0 ? r.range(14, 28) : r.range(372, 386);
      const w = r.range(9, 13);
      trunks += `M${pt(x - w / 2, 205)}C${pt(x - w * 0.42, 120)} ${pt(x - w * 0.36, 60)} ${pt(x - w * 0.3, -6)}L${pt(x + w * 0.3, -6)}C${pt(x + w * 0.36, 60)} ${pt(x + w * 0.42, 120)} ${pt(x + w / 2, 205)}Z`;
      trunks += `M${pt(x - side * w * 0.3, 70)}q${f1(-side * 10)} -6 ${f1(-side * 22)} -18l1.4 -1.6q${f1(side * 12)} 10 ${f1(side * 22)} 15Z`;
      for (let i = 0; i < 6; i++) leaves += circleD(x - side * r.range(0, 40), r.range(-8, 14), r.range(10, 18));
    }
    p.add(<g key={p.key()}><path d={leaves} fill={dark} /><path d={trunks} fill={bark} /></g>);
  }
}

function arcBandD(cx: number, cy: number, R: number, rr: number, a0: number, a1: number): string {
  const at = (rad: number, a: number): string => pt(cx + rad * Math.cos(a), cy - rad * Math.sin(a));
  const large = Math.abs(a0 - a1) > Math.PI ? 1 : 0;
  return `M${at(R, a0)}A${f1(R)} ${f1(R)} 0 ${large} 1 ${at(R, a1)}L${at(rr, a1)}A${f1(rr)} ${f1(rr)} 0 ${large} 0 ${at(rr, a0)}Z`;
}

function paintRuins(p: Painter, st: Stage): void {
  const e = p.env;
  const r = rngFor("ruines", e.seed, e.biome);
  groundUnder(p, st, st.sx - 82, st.sx + 82);
  const stone = p.tone(e.biome === "desert" ? "#c4a374" : e.biome === "volcan" ? "#6a5a56" : "#c2b7a0", 0.8);
  const shade = p.tone(e.biome === "volcan" ? "#3a302c" : "#857b66", 0.84);
  const b = new Batch();
  const g = st.base - 4;
  b.fill(stone, rectD(st.sx - 86, g + 1, 172, 5));
  b.fill(shade, rectD(st.sx - 86, g + 4, 172, 2), 0.7);
  b.fill(stone, rectD(st.sx - 78, g - 3, 156, 4));
  const tops: { x: number; top: number }[] = [];
  const pair = r.int(0, 3);
  for (let i = 0; i < 5; i++) {
    const x = st.sx - 62 + i * 31 + r.range(-2, 2);
    const full = i === pair || i === pair + 1;
    const h = full ? 44 : r.range(10, 32);
    const top = g - 3 - h;
    b.fill(stone, rectD(x - 3.6, top, 7.2, h) + rectD(x - 5, g - 6, 10, 3));
    b.fill(shade, rectD(e.lightFromLeft ? x + 1.2 : x - 3.6, top, 2.4, h), 0.6);
    if (full) b.fill(stone, rectD(x - 5.5, top - 3, 11, 3));
    else b.fill(stone, polyD([[x - 3.6, top + 0.5], [x - 1.8, top - 2.2], [x, top + 0.8], [x + 1.6, top - 3], [x + 3.6, top + 0.4], [x + 3.6, top + 3], [x - 3.6, top + 3]]));
    tops.push({ x, top });
  }
  const a = tops[pair];
  const c = tops[pair + 1];
  b.fill(stone, rectD(a.x - 6, a.top - 7.5, c.x - a.x + 12, 4.5));
  const ax = r.chance(0.5) ? st.sx - 106 : st.sx + 106;
  const R = 17;
  const rr = 11.5;
  const ay = g - 34;
  const broken = r.range(6, 14);
  b.fill(stone, rectD(ax - R, ay, R - rr, g - ay) + rectD(ax + rr, ay + broken, R - rr, g - ay - broken));
  b.fill(stone, arcBandD(ax, ay, R, rr, Math.PI, Math.PI * r.range(0.28, 0.42)));
  for (let i = 0; i < 4; i++) b.fill(shade, rectD(st.sx + r.range(-110, 110), g + r.range(-2, 3), r.range(5, 10), r.range(3, 5)));
  p.batch(b);
  if (e.biome !== "volcan" && e.biome !== "souterrain") {
    let ivy = "";
    for (const t of tops.slice(0, 3)) ivy += `M${pt(t.x + r.range(-2, 2), t.top + 2)}c${f1(r.range(-3, 3))} 6 ${f1(r.range(-3, 3))} 10 ${f1(r.range(-2, 2))} ${f1(r.range(14, 22))}`;
    p.add(<path key={p.key()} d={ivy} fill="none" stroke={p.tone(e.biome === "desert" ? "#9a8a5a" : "#5c7a3e", 0.82)} stroke-width="1.3" stroke-linecap="round" />);
  }
  st.bats = true;
}

function rockMoundD(r: Prng, cx: number, base: number, halfW: number, h: number): string {
  const pts: Pt[] = [];
  for (let i = 0; i <= 16; i++) {
    const t = i / 16;
    pts.push([cx - halfW + 2 * halfW * t, base - h * Math.pow(Math.sin(Math.PI * t), 0.8) * r.range(0.82, 1.08)]);
  }
  return polyD([[cx - halfW - 10, base + 30], ...pts, [cx + halfW + 10, base + 30]]);
}

function abyssFill(p: Painter): string {
  return p.def("abime", (id) => (
    <radialGradient id={id} cx="0.5" cy="0.75" r="0.75">
      <stop offset="0" stop-color="#000000" />
      <stop offset="0.7" stop-color="#07060a" />
      <stop offset="1" stop-color="#1a1418" />
    </radialGradient>
  ));
}

function windowGlows(p: Painter, r: Prng, windows: Pt[], radius = 6, color = LAMP_GLOW): void {
  if (!windows.length) return;
  const halo = p.glow(color, 0.65);
  p.add(
    <g key={p.key()}>
      {windows.map(([x, y], i) => (
        <circle key={i} class={i % 2 === 0 ? "scene-flicker" : undefined} cx={f1(x)} cy={f1(y)} r={f1(radius)} fill={halo}
          style={i % 2 === 0 ? anim(r.range(2.5, 5), -r.range(0, 4)) : undefined} />
      ))}
    </g>,
  );
}

function flagEls(p: Painter, r: Prng, tips: Pt[], color: string): El {
  const pole = p.tone("#3a3030", 0.85);
  return (
    <g key={p.key()}>
      {tips.map(([x, y], i) => (
        <g key={i}>
          <path d={`M${pt(x, y)}V${f1(y - 9)}`} stroke={pole} stroke-width="0.7" />
          <path class="scene-flag" d={`M${pt(x + 0.3, y - 9)}L${pt(x + 11, y - 6.8)}L${pt(x + 0.3, y - 4.6)}Z`} fill={color} style={anim(r.range(1.8, 2.6), -r.range(0, 2))} />
        </g>
      ))}
    </g>
  );
}

function paintDungeon(p: Painter, st: Stage): void {
  const e = p.env;
  const r = rngFor("donjon", e.seed, e.biome);
  const sx = st.sx;
  const g = st.base;
  const stoneBase = e.biome === "desert" ? "#c9ab7c" : e.biome === "volcan" ? "#5a4c48" : "#8f8a80";
  const stone = p.tone(stoneBase, 0.82);
  const shade = p.tone(mix(stoneBase, "#000000", 0.45), 0.86);
  p.add(<path key={p.key()} d={rockMoundD(r, sx, g + 2, 96, 44)} fill={p.tone(PAL[e.biome].rock, 0.76)} />);
  const b = new Batch();
  b.fill(stone, rectD(sx - 19, g - 38, 38, 40) + crenelsD(sx - 19, sx + 19, g - 38, 5, 4, 3.3));
  b.fill(shade, rectD(e.lightFromLeft ? sx + 8 : sx - 19, g - 38, 11, 40), 0.5);
  b.fill(shade, arcBandD(sx, g - 17.5, 11.5, 8.5, Math.PI, 0));
  b.fill(stone, rectD(sx - 15, g - 1, 30, 2.5) + rectD(sx - 12, g + 1.5, 24, 2.2));
  const banner = p.tone("#7a1f24", 0.84);
  b.fill(banner, polyD([[sx - 17, g - 34], [sx - 12, g - 34], [sx - 12, g - 19], [sx - 14.5, g - 22], [sx - 17, g - 19]]) + polyD([[sx + 12, g - 34], [sx + 17, g - 34], [sx + 17, g - 19], [sx + 14.5, g - 22], [sx + 12, g - 19]]));
  p.batch(b);
  p.add(<path key={p.key()} d={archD(sx, g, 17, 26)} fill={abyssFill(p)} />);
  let grid = "";
  for (let x = sx - 6; x <= sx + 6.1; x += 3) grid += `M${pt(x, g - 25)}V${f1(g - 12)}`;
  grid += `M${pt(sx - 8, g - 20)}H${f1(sx + 8)}M${pt(sx - 8, g - 15)}H${f1(sx + 8)}`;
  p.add(<path key={p.key()} d={grid} stroke={p.tone("#2c2c34", 0.9)} stroke-width="0.8" fill="none" />);
  p.add(torch(p, r, st, sx - 25, g - 22, 1, 10));
  p.add(torch(p, r, st, sx + 25, g - 22, 1, 10));
  st.bats = true;
}

function paintTomb(p: Painter, st: Stage): void {
  const e = p.env;
  const r = rngFor("tombeau", e.seed, e.biome);
  const sx = st.sx;
  const g = st.base;
  groundUnder(p, st, sx - 120, sx + 120);
  const tree = new Batch();
  addTree(tree, r, "mort", sx + (r.chance(0.5) ? -1 : 1) * r.range(62, 88), g - 1, r.range(40, 54), treeColors(p, 0.76), e.lightFromLeft);
  p.batch(tree);
  const stone = p.tone("#a8a196", 0.8);
  const shade = p.tone("#6e685f", 0.84);
  const b = new Batch();
  b.fill(shade, rectD(sx - 32, g - 3, 64, 4));
  b.fill(stone, rectD(sx - 28, g - 6, 56, 3.5) + rectD(sx - 24, g - 31, 48, 25) + rectD(sx - 27, g - 34, 54, 3) + polyD([[sx - 28, g - 34], [sx, g - 48], [sx + 28, g - 34]]));
  b.fill(shade, polyD([[sx - 22, g - 35.5], [sx, g - 45.5], [sx + 22, g - 35.5]]), 0.5);
  b.fill(mix(stone, "#ffffff", 0.08), rectD(sx - 19, g - 31, 3.5, 25) + rectD(sx + 15.5, g - 31, 3.5, 25));
  b.fill(shade, rectD(e.lightFromLeft ? sx + 6 : sx - 24, g - 31, 18, 25), 0.35);
  p.batch(b);
  p.add(<path key={p.key()} d={archD(sx, g - 6, 12, 18)} fill={abyssFill(p)} />);
  if (e.lamps) p.add(<circle key={p.key()} class="scene-glow-pulse" cx={f1(sx)} cy={f1(g - 13)} r="18" fill={p.glow("#9dffcf", 0.45)} style={anim(4, 0)} />);
  const marks: El[] = [];
  const markC = p.tone("#9a948a", 0.86);
  for (let i = 0; i < 8; i++) {
    let x = sx + r.range(-130, 130);
    if (Math.abs(x - sx) < 36) x += x < sx ? -36 : 36;
    const y = g + r.range(1, 7);
    const h = r.range(7, 12);
    const shape = r.int(0, 2);
    const d = shape === 0
      ? archD(x, y, h * 0.62, h)
      : shape === 1
        ? rectD(x - 0.9, y - h, 1.8, h) + rectD(x - h * 0.3, y - h * 0.72, h * 0.6, 1.8)
        : polyD([[x - 2.2, y], [x - 1.6, y - h * 0.85], [x, y - h * 1.05], [x + 1.6, y - h * 0.85], [x + 2.2, y]]);
    marks.push(<path key={i} d={d} fill={markC} transform={`rotate(${f1(r.range(-9, 9))} ${pt(x, y)})`} />);
  }
  p.add(<g key={p.key()}>{marks}</g>);
  p.add(torch(p, r, st, sx - 36, g - 14, 0.9, 14));
  p.add(torch(p, r, st, sx + 36, g - 14, 0.9, 14));
  st.mist = true;
  st.bats = true;
}

function paintGrotto(p: Painter, st: Stage): void {
  const e = p.env;
  const r = rngFor("grotte", e.seed, e.biome);
  const pal = PAL[e.biome];
  const sx = st.sx;
  const g = st.base + 2;
  const rock = p.tone(pal.rock, 0.74);
  p.add(<path key={p.key()} d={rockMoundD(r, sx, g, 125, 66)} fill={rock} />);
  let strata = "";
  for (let i = 0; i < 6; i++) {
    const y = g - r.range(14, 54);
    const x = sx + r.range(-90, 90);
    strata += `M${pt(x - r.range(10, 24), y)}q${f1(r.range(8, 14))} ${f1(r.range(-2, 2))} ${f1(r.range(20, 40))} ${f1(r.range(-1, 2))}`;
  }
  p.add(<path key={p.key()} d={strata} stroke={p.tone("#2a2630", 0.8)} stroke-width="0.8" fill="none" opacity="0.6" />);
  const mouth: Pt[] = [[sx - 25, g], [sx - 28, g - 14], [sx - 23, g - 29], [sx - 11, g - 38], [sx + 3, g - 41], [sx + 16, g - 34], [sx + 26, g - 19], [sx + 27, g]];
  const md = `${smoothD(mouth)}Z`;
  p.add(<path key={p.key()} d={md} fill="none" stroke={p.tone(mix(pal.rock, "#ffffff", 0.2), 0.74)} stroke-width="2.6" />);
  p.add(<path key={p.key()} d={md} fill={abyssFill(p)} />);
  let teeth = "";
  for (let x = sx - 18; x <= sx + 17; x += r.range(4, 6.5)) {
    const y = g - 39 + 26 * Math.pow((x - sx) / 27, 2);
    teeth += polyD([[x - 2, y - 1.5], [x + r.range(-0.6, 0.6), y + r.range(3, 6.5)], [x + 2, y - 1.5]]);
  }
  p.add(<path key={p.key()} d={teeth} fill={rock} />);
  let rocks = "";
  for (let i = 0; i < 3; i++) {
    const x = sx + (i - 1) * r.range(45, 70) + r.range(-8, 8);
    rocks += polyD(Array.from({ length: 8 }, (_, k): Pt => {
      const a = (Math.PI * k) / 7;
      const w = 7 + i * 2;
      return [x - Math.cos(a) * w * r.range(0.85, 1.05), g + 4 - Math.sin(a) * w * 0.6 * r.range(0.85, 1.1)];
    }));
  }
  p.add(<path key={p.key()} d={rocks} fill={p.tone(pal.rock, 0.84)} />);
  p.add(torch(p, r, st, sx - 36, g - 18, 1, 16));
  p.add(torch(p, r, st, sx + 37, g - 16, 1, 14));
  st.bats = true;
}

function paintTower(p: Painter, st: Stage): void {
  const e = p.env;
  const r = rngFor("tour", e.seed, e.biome);
  const sx = st.sx;
  const g = st.base;
  p.add(<path key={p.key()} d={rockMoundD(r, sx, g + 2, 52, 16)} fill={p.tone(PAL[e.biome].rock, 0.76)} />);
  const tb = g - 12;
  const top = tb - 86;
  const stoneBase = e.biome === "volcan" ? "#5a4e4a" : e.biome === "desert" ? "#d2b48a" : "#a7a091";
  const stone = p.tone(stoneBase, 0.8);
  const shade = p.tone(mix(stoneBase, "#000000", 0.45), 0.84);
  const roofC = p.tone(e.biome === "souterrain" ? "#3d3560" : "#3b3f63", 0.82);
  const dark = p.tone("#1c1d24", 0.85);
  const b = new Batch();
  const windows: Pt[] = [];
  if (r.chance(0.6)) {
    const ax = sx + (r.chance(0.5) ? 19 : -19);
    b.fill(stone, rectD(ax - 8, tb - 8, 16, 13));
    b.fill(roofC, polyD([[ax - 10, tb - 8], [ax, tb - 17], [ax + 10, tb - 8]]));
    const lit = e.lamps;
    b.fill(lit ? LAMP : dark, rectD(ax - 1.2, tb - 4, 2.4, 3));
    if (lit) windows.push([ax, tb - 2.5]);
  }
  b.fill(stone, polyD([[sx - 12, tb + 3], [sx - 9, top], [sx + 9, top], [sx + 12, tb + 3]]));
  b.fill(shade, e.lightFromLeft ? polyD([[sx + 3.5, tb + 3], [sx + 3, top], [sx + 9, top], [sx + 12, tb + 3]]) : polyD([[sx - 12, tb + 3], [sx - 9, top], [sx - 3, top], [sx - 3.5, tb + 3]]), 0.45);
  b.stroke(shade, 0.6, `M${pt(sx - 11.3, tb - 22)}H${f1(sx + 11.3)}M${pt(sx - 10.5, tb - 48)}H${f1(sx + 10.5)}M${pt(sx - 9.8, tb - 70)}H${f1(sx + 9.8)}`);
  b.fill(stone, rectD(sx - 12.5, top - 1, 25, 3) + crenelsD(sx - 12.5, sx + 12.5, top - 1, 2.6, 2.4, 2));
  b.fill(roofC, `M${pt(sx - 13, top - 3.4)}Q${pt(sx - 4, top - 10)} ${pt(sx + 1.5, top - 34)}Q${pt(sx + 4, top - 12)} ${pt(sx + 13, top - 3.4)}Z`);
  for (const [x, y] of [[sx - 3, tb - 12], [sx + 3, tb - 36], [sx - 2, tb - 60]] as Pt[]) {
    b.fill(e.lamps ? LAMP : dark, archD(x, y, 2.8, 5));
    if (e.lamps) windows.push([x, y - 2.5]);
  }
  const magic = e.biome === "volcan" ? "#ff9a5a" : "#c9a2ff";
  b.fill(e.lamps ? magic : dark, archD(sx, top + 12, 4, 7));
  p.batch(b);
  windowGlows(p, r, windows, 5);
  if (e.lamps) p.add(<circle key={p.key()} class="scene-glow-pulse" cx={f1(sx)} cy={f1(top + 8)} r="26" fill={p.glow(magic, 0.55)} style={anim(3.8, 0)} />);
  p.add(flagEls(p, r, [[sx + 1.5, top - 34]], p.tone("#b0302a", 0.8)));
  if (e.night) st.motes.push({ x: sx, y: top + 4, spread: 16, count: 5, rise: 22, colors: ["#e3d2ff", "#a68cff"] });
}

function paintCastle(p: Painter, st: Stage): void {
  const e = p.env;
  const r = rngFor("chateau", e.seed, e.biome);
  const pal = PAL[e.biome];
  const sx = st.sx;
  const g = st.base - 6;
  p.add(<path key={p.key()} d={rockMoundD(r, sx, st.base + 4, 140, 14)} fill={p.tone(mix(pal.mid, pal.rock, 0.4), 0.72, pal.albedo)} />);
  const stoneBase = e.biome === "desert" ? "#d6b98c" : e.biome === "volcan" ? "#4e4444" : e.biome === "souterrain" ? "#6b6480" : "#a7a196";
  const back = p.tone(stoneBase, 0.7);
  const front = p.tone(stoneBase, 0.8);
  const shade = p.tone(mix(stoneBase, "#000000", 0.4), 0.84);
  const roofC = p.tone(r.chance(0.5) ? "#3f4a66" : "#7a2e2a", 0.8);
  const dark = p.tone("#16171d", 0.86);
  const windows: Pt[] = [];
  const flags: Pt[] = [];
  const kb = new Batch();
  kb.fill(back, rectD(sx - 16, g - 60, 32, 60) + crenelsD(sx - 17, sx + 17, g - 60, 3.4, 3.2, 2.7));
  const kx = sx + (r.chance(0.5) ? -8 : 8);
  kb.fill(back, rectD(kx - 5, g - 80, 10, 22));
  kb.fill(roofC, polyD([[kx - 7, g - 79.5], [kx, g - 100], [kx + 7, g - 79.5]]));
  flags.push([kx, g - 100]);
  kb.fill(shade, rectD(e.lightFromLeft ? sx + 5 : sx - 16, g - 60, 11, 60), 0.4);
  for (const [x, y] of [[sx - 8, g - 50], [sx + 8, g - 50], [sx, g - 38], [kx, g - 72]] as Pt[]) {
    const lit = e.lamps && r.chance(0.8);
    kb.fill(lit ? LAMP : dark, archD(x, y + 5, 2.4, 5));
    if (lit) windows.push([x, y + 2.5]);
  }
  p.batch(kb);
  const fb = new Batch();
  const wallTop = g - 22;
  fb.fill(front, rectD(sx - 84, wallTop, 168, 26) + crenelsD(sx - 84, sx + 84, wallTop, 3.2, 3, 2.6));
  [sx - 88, sx - 42, sx + 42, sx + 88].forEach((tx, i) => {
    const outer = i === 0 || i === 3;
    const w = outer ? 18 : 14;
    const h = outer ? 44 : 34;
    const top = g - h;
    fb.fill(front, rectD(tx - w / 2, top, w, h + 4));
    fb.fill(shade, rectD(e.lightFromLeft ? tx + w * 0.15 : tx - w / 2, top, w * 0.35, h + 4), 0.45);
    if (outer || r.chance(0.5)) {
      fb.fill(roofC, polyD([[tx - w / 2 - 2, top + 0.5], [tx, top - w * 1.25], [tx + w / 2 + 2, top + 0.5]]));
      if (outer) flags.push([tx, top - w * 1.25]);
    } else fb.fill(front, crenelsD(tx - w / 2 - 1, tx + w / 2 + 1, top, 2.8, 2.8, 2.2));
    const lit = e.lamps && r.chance(0.75);
    fb.fill(lit ? LAMP : dark, rectD(tx - 0.8, top + 9, 1.6, 4.5));
    if (lit) windows.push([tx, top + 11]);
  });
  p.batch(fb);
  p.add(<path key={p.key()} d={archD(sx, g + 4, 14, 17)} fill={abyssFill(p)} />);
  let grid = "";
  for (let x = sx - 5; x <= sx + 5.1; x += 2.5) grid += `M${pt(x, g - 11)}V${f1(g - 3)}`;
  p.add(<path key={p.key()} d={grid} stroke={p.tone("#2c2c34", 0.9)} stroke-width="0.7" fill="none" />);
  windowGlows(p, r, windows, 5);
  p.add(flagEls(p, r, flags, p.tone(r.chance(0.5) ? "#b0302a" : "#d9b45a", 0.8)));
}

function paintSanctuary(p: Painter, st: Stage): void {
  const e = p.env;
  const r = rngFor("sanctuaire", e.seed, e.biome);
  const sx = st.sx;
  const g = st.base;
  const holy = e.biome === "volcan" ? "#ffb35a" : e.biome === "souterrain" ? "#9fe8ff" : e.danger ? "#ff9a8a" : "#ffe7a3";
  groundUnder(p, st, sx - 60, sx + 60);
  p.add(<circle key={p.key()} class="scene-halo" cx={f1(sx)} cy={f1(g - 36)} r="64" fill={p.glow(holy, e.night ? 0.6 : 0.42)} style={anim(6, 0)} />);
  let rays = "";
  for (let i = 0; i < 12; i++) {
    const a = (Math.PI * 2 * i) / 12 + 0.2;
    rays += `M${pt(sx + Math.cos(a) * 26, g - 36 - Math.sin(a) * 26)}L${pt(sx + Math.cos(a) * 58, g - 36 - Math.sin(a) * 58)}`;
  }
  p.add(<path key={p.key()} class="scene-glow-pulse" d={rays} stroke={p.glow(holy, 0.9)} stroke-width="1.4" opacity="0.5" stroke-linecap="round" style={anim(5, -2)} />);
  const stoneBase = e.biome === "desert" ? "#e2cfa6" : e.biome === "souterrain" ? "#8a83a0" : "#d8d2c4";
  const stone = p.tone(stoneBase, 0.8);
  const shade = p.tone(mix(stoneBase, "#000000", 0.35), 0.84);
  let menhirs = "";
  const nm = r.int(2, 4);
  for (let i = 0; i < nm; i++) {
    const x = sx + (i % 2 ? 1 : -1) * r.range(56, 96);
    const h = r.range(12, 22);
    menhirs += `M${pt(x - 4, g + 2)}Q${pt(x - 5.5, g - h * 0.7)} ${pt(x - 1, g - h)}Q${pt(x + 4.5, g - h * 0.8)} ${pt(x + 4, g + 2)}Z`;
  }
  p.add(<path key={p.key()} d={menhirs} fill={p.tone(PAL[e.biome].rock, 0.8)} />);
  const tholos = r.chance(0.4);
  p.add(<rect key={p.key()} x={f1(sx - 22)} y={f1(g - 35)} width="44" height="27" fill={shade} />);
  p.add(<rect key={p.key()} class="scene-glow-pulse" x={f1(sx - 22)} y={f1(g - 35)} width="44" height="27" fill={p.glow(holy, 0.9)} opacity={e.lamps ? "1" : "0.6"} style={anim(4.5, -1)} />);
  const b = new Batch();
  b.fill(shade, rectD(sx - 40, g - 3, 80, 3.5));
  b.fill(stone, rectD(sx - 34, g - 6, 68, 3) + rectD(sx - 28, g - 9, 56, 3));
  if (tholos) {
    for (let i = 0; i < 5; i++) b.fill(stone, rectD(sx - 21.6 + i * 10, g - 33, 3.2, 24));
    b.fill(stone, rectD(sx - 25, g - 37, 50, 4.5) + `M${pt(sx - 23, g - 37)}a23 18 0 0 1 46 0Z`);
    b.fill(shade, `M${pt(e.lightFromLeft ? sx : sx - 23, g - 37)}a${e.lightFromLeft ? "23 18 0 0 1 23 0" : "23 18 0 0 1 23 -18"}V${f1(g - 37)}Z`, 0.3);
    b.fill(p.tone("#d9b45a", 0.7), circleD(sx, g - 56.5, 1.6));
  } else {
    for (const x of [sx - 21, sx - 7, sx + 7, sx + 21]) b.fill(stone, rectD(x - 2.5, g - 35, 5, 26) + rectD(x - 3.5, g - 37, 7, 2.2));
    b.fill(stone, rectD(sx - 27, g - 41, 54, 4) + polyD([[sx - 29, g - 41], [sx, g - 54], [sx + 29, g - 41]]));
    b.fill(shade, polyD([[sx - 23, g - 42.4], [sx, g - 51.5], [sx + 23, g - 42.4]]), 0.4);
  }
  p.batch(b);
  st.motes.push({ x: sx, y: g - 16, spread: 30, count: 8, rise: 42, colors: [holy, "#ffffff"] });
}

function shipEls(p: Painter, r: Prng, x: number, wl: number, L: number, masts: number, flip: boolean): El {
  const e = p.env;
  const hull = p.tone("#4a3426", 0.82);
  const wood = p.tone("#3a2a1e", 0.84);
  const sail = p.tone("#efe6d2", 0.8);
  const rope = p.tone("#2a2420", 0.84);
  const s = flip ? -1 : 1;
  const bowX = x + s * (L / 2 + 3);
  const b = new Batch();
  b.fill(hull, `M${pt(x - s * L / 2, wl - 4)}L${pt(bowX, wl - 5.5)}Q${pt(x + s * (L / 2 - 1), wl + 2)} ${pt(x + s * (L / 2 - 6), wl + 2.5)}L${pt(x - s * (L / 2 - 3), wl + 2.5)}Q${pt(x - s * (L / 2 + 1.5), wl)} ${pt(x - s * L / 2, wl - 4)}Z`);
  b.fill(wood, rectD(Math.min(x - s * L / 2 + s, x - s * L / 2 + s * L * 0.26), wl - 8.5, L * 0.25, 4.6));
  b.stroke(p.tone("#d9b45a", 0.8), 0.5, `M${pt(x - s * L / 2, wl - 2.5)}L${pt(bowX - s * 1.5, wl - 3.6)}`, 0.7);
  const furled = e.night || e.weather === "orage" || r.chance(0.4);
  let rig = "";
  for (let m = 0; m < masts; m++) {
    const mx = x - s * L * 0.18 + s * m * L * 0.36;
    const H = L * 0.95 - m * 5;
    const top = wl - 4 - H;
    b.stroke(wood, 1, `M${pt(mx, wl - 4)}V${f1(top)}`);
    const yw = L * 0.32;
    b.stroke(wood, 0.6, `M${pt(mx - yw / 2, top + 4)}h${f1(yw)}M${pt(mx - yw * 0.6, top + H * 0.45)}h${f1(yw * 1.2)}`);
    if (furled) b.fill(sail, rectD(mx - yw / 2, top + 3.2, yw, 1.6) + rectD(mx - yw * 0.6, top + H * 0.45 - 0.8, yw * 1.2, 1.6));
    else b.fill(sail, polyD([[mx - yw / 2, top + 4.4], [mx + yw / 2, top + 4.4], [mx + yw * 0.62 + s, top + H * 0.42], [mx - yw * 0.62 + s, top + H * 0.42]]) + polyD([[mx - yw * 0.6, top + H * 0.46], [mx + yw * 0.6, top + H * 0.46], [mx + yw * 0.66 + s, top + H * 0.8], [mx - yw * 0.66 + s, top + H * 0.8]]));
    rig += `M${pt(mx, top)}L${pt(m === masts - 1 ? bowX : mx + s * L * 0.36, m === masts - 1 ? wl - 5.5 : top + 6)}`;
  }
  b.stroke(rope, 0.35, rig, 0.8);
  const sternX = x - s * L / 2 + s * 2;
  return (
    <g key={p.key()} class="scene-bob" style={anim(r.range(4, 6.5), -r.range(0, 5))}>
      {b.els()}
      {e.lamps ? <circle cx={f1(sternX)} cy={f1(wl - 10)} r="8" fill={p.glow(LAMP_GLOW, 0.7)} /> : null}
      {e.lamps ? <circle cx={f1(sternX)} cy={f1(wl - 10)} r="1.1" fill={LAMP} /> : null}
    </g>
  );
}

function paintPort(p: Painter, st: Stage): void {
  const e = p.env;
  const r = rngFor("port", e.seed, e.biome);
  const left = st.sx > 200;
  const deckY = 157;
  const qx = left ? st.sx - 8 : st.sx + 8;
  const x0 = left ? -20 : qx;
  const x1 = left ? qx : 420;
  const wood = p.tone("#6a4c34", 0.84);
  const dark = p.tone("#3a2a1e", 0.86);
  const style = houseStyle(e.biome, e.weather);
  const windows: Pt[] = [];
  const chimneys: Pt[] = [];
  for (let i = 0; i < 3; i++) {
    const hx = left ? 30 + i * 34 + r.range(-4, 4) : 370 - i * 34 + r.range(-4, 4);
    if ((left && hx > qx - 14) || (!left && hx < qx + 14)) continue;
    const b = new Batch();
    addHouse(b, r, { x: hx, base: deckY, w: r.range(18, 24), h: r.range(12, 16), tint: r.int(0, 5), chimney: r.chance(0.5), side: r.chance(0.6), back: false, dome: false }, { ...style, stilts: false }, houseColors(p, style, i, 0.8), e, windows, chimneys);
    p.batch(b);
  }
  windowGlows(p, r, windows, 5.5);
  let posts = "";
  for (let x = x0 + 4; x < x1; x += 8) posts += rectD(x - 0.7, deckY + 2, 1.4, 12);
  p.add(<g key={p.key()}><path d={posts} fill={dark} /><path d={rectD(x0, deckY, x1 - x0, 3)} fill={wood} /></g>);
  const goods = new Batch();
  const gx = left ? qx - 16 : qx + 10;
  goods.fill(p.tone("#8a6a44", 0.84), rectD(gx, deckY - 4, 5, 4) + rectD(gx + 5.5, deckY - 3, 3.5, 3));
  goods.fill(p.tone("#5d4330", 0.84), ellipseD(gx + 11, deckY - 2.2, 1.9, 2.3));
  p.batch(goods);
  if (e.lamps) {
    const lx = left ? qx - 2 : qx + 2;
    p.add(<g key={p.key()}><path d={`M${pt(lx, deckY)}V${f1(deckY - 12)}`} stroke={dark} stroke-width="0.9" /><circle cx={f1(lx)} cy={f1(deckY - 13)} r="9" fill={p.glow(LAMP_GLOW, 0.7)} class="scene-flicker" style={anim(3, 0)} /><circle cx={f1(lx)} cy={f1(deckY - 13)} r="1.2" fill={LAMP} /></g>);
  }
  const dir = left ? 1 : -1;
  p.add(shipEls(p, r, qx + dir * r.range(48, 62), 167, r.range(40, 50), 2, !left));
  if (r.chance(0.7)) p.add(shipEls(p, r, qx + dir * r.range(112, 132), 160, r.range(26, 32), 1, left));
  if (st.water === "mer" && st.cliffTop) {
    const [lx, ly] = st.cliffTop;
    const b = new Batch();
    const white = p.tone("#ece6da", 0.6);
    const red = p.tone("#b0402f", 0.6);
    b.fill(white, polyD([[lx - 4.5, ly + 1], [lx - 3, ly - 28], [lx + 3, ly - 28], [lx + 4.5, ly + 1]]));
    b.fill(red, polyD([[lx - 4.2, ly - 6], [lx - 3.9, ly - 12], [lx + 3.9, ly - 12], [lx + 4.2, ly - 6]]) + polyD([[lx - 3.6, ly - 18], [lx - 3.3, ly - 23], [lx + 3.3, ly - 23], [lx + 3.6, ly - 18]]));
    b.fill(p.tone("#2a2a30", 0.6), rectD(lx - 4.5, ly - 29.5, 9, 1.6) + polyD([[lx - 3, ly - 33], [lx, ly - 36.5], [lx + 3, ly - 33]]));
    b.fill(e.lamps ? LAMP : p.tone("#c9d6e0", 0.6), rectD(lx - 2.4, ly - 33, 4.8, 3.6));
    p.batch(b);
    if (e.lamps) p.add(<circle key={p.key()} class="scene-glow-pulse" cx={f1(lx)} cy={f1(ly - 31)} r="26" fill={p.glow("#ffe2a0", 0.7)} style={anim(2.6, 0)} />);
  }
}

function paintLakeSide(p: Painter, st: Stage): void {
  const e = p.env;
  const r = rngFor("lac", e.seed, e.biome);
  const left = r.chance(0.5);
  const wood = p.tone("#6a4c34", 0.86);
  const dark = p.tone("#3a2a1e", 0.88);
  const deckY = 176;
  const x0 = left ? -20 : st.sx + 28;
  const x1 = left ? st.sx - 28 : 420;
  let posts = "";
  for (let x = x0 + 4; x < x1; x += 9) posts += rectD(x - 0.8, deckY + 2, 1.6, 10);
  p.add(<g key={p.key()}><path d={posts} fill={dark} /><path d={rectD(x0, deckY, x1 - x0, 2.8)} fill={wood} /></g>);
  const endX = left ? x1 - 3 : x0 + 3;
  if (e.lamps) {
    p.add(<g key={p.key()}><path d={`M${pt(endX, deckY)}V${f1(deckY - 13)}`} stroke={dark} stroke-width="1" /><circle class="scene-flicker" cx={f1(endX)} cy={f1(deckY - 14)} r="12" fill={p.glow(LAMP_GLOW, 0.65)} style={anim(3.4, 0)} /><circle cx={f1(endX)} cy={f1(deckY - 14)} r="1.3" fill={LAMP} /></g>);
  }
  const bx = st.sx + (left ? 34 : -34) + r.range(-12, 12);
  const by = 163;
  const boat = p.tone("#5a3e2a", 0.8);
  p.add(
    <g key={p.key()} class="scene-bob" style={anim(5.5, -r.range(0, 5))}>
      <path d={`M${pt(bx - 13, by - 2.5)}H${f1(bx + 13)}Q${pt(bx + 10, by + 3)} ${pt(bx + 6, by + 3.4)}H${f1(bx - 6)}Q${pt(bx - 10, by + 3)} ${pt(bx - 13, by - 2.5)}Z`} fill={boat} />
      <path d={`M${pt(bx - 9, by - 7)}L${pt(bx + 4, by + 5)}`} stroke={dark} stroke-width="0.8" stroke-linecap="round" />
    </g>,
  );
  const ripple = e.night ? "#9fb2dd" : "#eaf6fb";
  const rings: El[] = [];
  for (let i = 0; i < 3; i++) {
    const dur = r.range(4, 7);
    rings.push(<ellipse key={i} class="scene-ripple" cx={f1(r.range(60, 340))} cy={f1(r.range(150, 172))} rx="9" ry="1.8" fill="none" stroke={ripple} stroke-width="0.6" opacity="0.3" style={anim(dur, -r.range(0, dur))} />);
  }
  p.add(<g key={p.key()}>{rings}</g>);
}

function paintPass(p: Painter, st: Stage): void {
  const e = p.env;
  const r = rngFor("col", e.seed, e.biome);
  const sx = st.sx;
  const snowy = e.weather === "neige" || PAL[e.biome].albedo > 0.3;
  const trail = p.tone(snowy ? "#e9eef4" : "#cdbf9c", 0.7, snowy ? 1 : 0);
  const top = st.saddleY + 3;
  p.add(
    <g key={p.key()} fill="none" stroke={trail} stroke-linecap="round" opacity="0.9">
      <path d={`M${pt(sx + 14, 205)}C${pt(sx + 40, 186)} ${pt(sx - 30, 176)} ${pt(sx - 4, 166)}`} stroke-width="5" />
      <path d={`M${pt(sx - 4, 166)}C${pt(sx + 16, 158)} ${pt(sx - 14, 150)} ${pt(sx + 2, 142)}`} stroke-width="3" />
      <path d={`M${pt(sx + 2, 142)}C${pt(sx + 10, 134)} ${pt(sx - 6, 126)} ${pt(sx, top)}`} stroke-width="1.6" />
    </g>,
  );
  let cairn = "";
  const cx = sx + 36;
  [6, 5, 4.2, 3.2, 2.2].forEach((w, i) => {
    cairn += ellipseD(cx + r.range(-0.8, 0.8), st.base - 1 - i * 3.2, w, 1.9);
  });
  p.add(<path key={p.key()} d={cairn} fill={p.tone("#8a8c94", 0.82)} />);
  const x = sx - 34;
  const y = st.base;
  const wood = p.tone("#6a4c34", 0.84);
  p.add(
    <g key={p.key()}>
      <path d={`M${pt(x, y + 2)}V${f1(y - 20)}`} stroke={wood} stroke-width="1.6" stroke-linecap="round" />
      <path d={polyD([[x - 1, y - 18], [x + 11, y - 18], [x + 14, y - 16], [x + 11, y - 14], [x - 1, y - 14]]) + polyD([[x + 1, y - 12], [x - 11, y - 12], [x - 14, y - 10], [x - 11, y - 8], [x + 1, y - 8]])} fill={wood} />
    </g>,
  );
}

function paintCamp(p: Painter, st: Stage): void {
  const e = p.env;
  const r = rngFor("camp", e.seed, e.biome);
  const fx = st.sx;
  const g = st.base;
  const fy = g + 5;
  groundUnder(p, st, fx - 110, fx + 110);
  p.add(<ellipse key={p.key()} class="scene-flicker" cx={f1(fx)} cy={f1(fy)} rx="84" ry="17" fill={p.glow("#ff9a3d", e.lamps ? 0.5 : 0.22)} style={anim(2.4, 0)} />);
  const canvas = ["#c9b48a", "#8b3a3a", "#3f5f7a", "#7a6a3a"];
  const n = r.int(2, 3);
  const spots = n === 2 ? [-52, 50] : [-56, 48, r.chance(0.5) ? -104 : 100];
  spots.forEach((dx, i) => {
    const x = fx + dx + r.range(-6, 6);
    const w = r.range(28, 36);
    const h = w * r.range(0.62, 0.72);
    const base = g + r.range(-2, 1);
    const tb = new Batch();
    tb.fill(p.tone(r.pick(canvas), i === 2 ? 0.74 : 0.8), polyD([[x - w / 2, base], [x, base - h], [x + w / 2, base]]));
    const toward = fx > x ? 1 : -1;
    if (e.lamps) tb.fill("#ff9a3d", polyD([[x, base - h], [x + toward * w / 2, base], [x + toward * w * 0.1, base]]), 0.28);
    else tb.fill(e.shade, polyD([[x, base - h], [x - toward * w / 2, base], [x - toward * w * 0.1, base]]), 0.22);
    tb.fill(e.lamps && r.chance(0.5) ? LAMP : p.tone("#15131a", 0.85), polyD([[x - w * 0.15, base], [x, base - h * 0.6], [x + w * 0.15, base]]));
    tb.stroke(p.tone("#4a3626", 0.85), 0.8, `M${pt(x, base - h - 3)}V${f1(base - h + 1)}`);
    tb.stroke(p.tone("#d8cdb4", 0.8), 0.35, `M${pt(x, base - h)}L${pt(x - w * 0.75, base + 1)}M${pt(x, base - h)}L${pt(x + w * 0.75, base + 1)}`, 0.6);
    p.batch(tb);
  });
  const wood = p.tone("#4a3626", 0.86);
  const b = new Batch();
  for (const dx of [-26, 26]) b.fill(wood, `M${pt(fx + dx - 8, fy + 2)}h16a2 2 0 0 1 0 4h-16a2 2 0 0 1 0 -4Z`);
  let stones = "";
  for (let i = 0; i < 8; i++) {
    const a = Math.PI * (i / 7);
    stones += ellipseD(fx - Math.cos(a) * 10, fy + 1.5 - Math.sin(a) * 1.5, 2.2, 1.4);
  }
  b.fill(p.tone("#77736c", 0.88), stones);
  b.stroke(p.tone("#2e2018", 0.9), 2, `M${pt(fx - 7, fy + 1)}L${pt(fx + 7, fy - 2)}M${pt(fx - 7, fy - 2)}L${pt(fx + 7, fy + 1)}`);
  p.batch(b);
  p.add(
    <g key={p.key()}>
      <circle class="scene-flicker" cx={f1(fx)} cy={f1(fy - 6)} r="30" fill={p.glow("#ffb347", 0.75)} style={anim(1.9, 0)} />
      <path class="scene-flame" d={flameD(fx, fy, 6.5, 15)} fill="#ff5a1f" style={anim(0.9, 0)} />
      <path class="scene-flame" d={flameD(fx + 0.5, fy, 4.5, 11)} fill="#ff9a3d" style={anim(0.7, -0.3)} />
      <path class="scene-flame" d={flameD(fx, fy, 2.6, 7)} fill="#ffe08a" style={anim(0.55, -0.2)} />
    </g>,
  );
  p.add(<g key={p.key()}>{smokePuffs(r, fx + 2, fy - 18, 3, e.night ? "#5d6274" : "#cfd3d8", 2.6, 26, 0.3)}</g>);
  st.embers.push({ x: fx, y: fy - 10, spread: 5, count: 10, rise: 40, colors: ["#ffd27a", "#ffb347", "#ff7a2f"] });
}

function paintSubject(p: Painter, st: Stage): void {
  switch (p.env.kind) {
    case "village":
    case "ville":
    case "hameau":
      return paintSettlement(p, st);
    case "foret":
      return paintForestKind(p, st, false);
    case "clairiere":
      return paintForestKind(p, st, true);
    case "ruines":
      return paintRuins(p, st);
    case "donjon":
      return paintDungeon(p, st);
    case "tombeau":
      return paintTomb(p, st);
    case "grotte":
      return paintGrotto(p, st);
    case "tour":
      return paintTower(p, st);
    case "chateau":
      return paintCastle(p, st);
    case "sanctuaire":
      return paintSanctuary(p, st);
    case "port":
      return paintPort(p, st);
    case "lac":
      return paintLakeSide(p, st);
    case "col":
      return paintPass(p, st);
    case "camp":
      return paintCamp(p, st);
  }
}

// ───────── Premier plan ─────────

function grassD(r: Prng, pts: Pt[], x0: number, x1: number, step: number, hmin: number, hmax: number): string {
  let d = "";
  for (let x = x0; x < x1; x += step * r.range(0.6, 1.4)) {
    const y = yAt(pts, x) + 1.2;
    d += `M${pt(x - 0.7, y)}L${pt(x + r.range(-1.6, 1.6), y - r.range(hmin, hmax))}L${pt(x + 0.7, y)}Z`;
  }
  return d;
}

function rockD(r: Prng, x: number, base: number, w: number, h: number): string {
  const pts: Pt[] = [];
  for (let i = 0; i <= 7; i++) {
    const a = (Math.PI * i) / 7;
    const k = r.range(0.82, 1.08);
    pts.push([x - Math.cos(a) * w * 0.5 * k, base - Math.sin(a) * h * k]);
  }
  return polyD(pts);
}

function leafD(x: number, y: number, len: number, a: number, w: number): string {
  const c = Math.cos(a);
  const s = Math.sin(a);
  const mx = x + c * len * 0.5;
  const my = y - s * len * 0.5;
  return `M${pt(x, y)}Q${pt(mx - s * w, my - c * w)} ${pt(x + c * len, y - s * len)}Q${pt(mx + s * w, my + c * w)} ${pt(x, y)}Z`;
}

/** Touffe de roseaux qui ondule (petite, pour que la rotation reste naturelle). */
function reedClump(p: Painter, r: Prng, x: number, base: number, depth: number): void {
  const stem = p.tone(p.env.biome === "desert" ? "#8a8a4a" : "#4f6a34", depth);
  const head = p.tone("#4a3222", depth);
  let stems = "";
  let heads = "";
  for (let i = 0; i < 6; i++) {
    const sx = x + r.range(-6, 6);
    const h = r.range(9, 20);
    const lean = r.range(-3, 3);
    stems += `M${pt(sx, base + r.range(0, 3))}q${f1(lean * 0.3)} ${f1(-h * 0.5)} ${f1(lean)} ${f1(-h)}`;
    if (r.chance(0.45)) heads += ellipseD(sx + lean * 0.95, base - h + 2.6, 0.9, 2.6);
  }
  const dur = r.range(5, 8);
  p.add(
    <g key={p.key()} class="scene-sway-up" style={anim(dur, -r.range(0, dur))}>
      <path d={stems} fill="none" stroke={stem} stroke-width="0.7" stroke-linecap="round" />
      {heads ? <path d={heads} fill={head} /> : null}
    </g>,
  );
}

function flowers(p: Painter, r: Prng, pts: Pt[]): void {
  const colors = ["#f2d36b", "#e98aa0", "#ffffff", "#b58cf0"];
  const byColor = new Map<string, string>();
  for (let i = 0; i < 40; i++) {
    const x = r.range(-10, 410);
    const c = p.tone(r.pick(colors), 0.92);
    byColor.set(c, (byColor.get(c) ?? "") + circleD(x, yAt(pts, x) + r.range(2, 14), r.range(0.6, 1.1)));
  }
  p.add(<g key={p.key()}>{[...byColor].map(([c, d], i) => <path key={i} d={d} fill={c} />)}</g>);
}

function jungleFrame(p: Painter, r: Prng): void {
  const dark = p.tone("#1c4a2a", 1);
  const leafC = p.tone("#245c32", 1);
  let canopy = "";
  for (let x = -20; x < 430; x += r.range(10, 18)) canopy += circleD(x, r.range(-8, 4), r.range(8, 15));
  p.add(<path key={p.key()} d={canopy} fill={dark} />);
  const els: El[] = [];
  for (let i = 0; i < 6; i++) {
    const x = i < 3 ? r.range(10, 120) : r.range(280, 390);
    const len = r.range(28, 80);
    let leaves = "";
    for (let k = 1; k < 5; k++) {
      const t = k / 5;
      leaves += ellipseD(x + Math.sin(t * 5) * 3 + 2, len * t, 2.2, 1.1);
    }
    const dur = r.range(6, 9);
    els.push(
      <g key={i} class="scene-sway" style={anim(dur, -r.range(0, dur))}>
        <path d={`M${pt(x, -2)}C${pt(x + 8, len * 0.3)} ${pt(x - 8, len * 0.7)} ${pt(x + r.range(-4, 4), len)}`} fill="none" stroke={leafC} stroke-width="1.1" />
        <path d={leaves} fill={leafC} />
      </g>,
    );
  }
  p.add(<g key={p.key()}>{els}</g>);
  let big = "";
  for (const corner of [-1, 1]) {
    for (let i = 0; i < 3; i++) {
      const a = corner < 0 ? r.range(0.3, 1.1) : Math.PI - r.range(0.3, 1.1);
      big += leafD(corner < 0 ? 6 : 394, 206, r.range(30, 46), a, r.range(7, 11));
    }
  }
  p.add(<path key={p.key()} d={big} fill={dark} />);
}

function stalactites(r: Prng, top: number, maxLen: number): { d: string; tips: Pt[] } {
  let d = `M-30 -10L430 -10L430 ${f1(top)}`;
  const tips: Pt[] = [];
  let x = 430;
  while (x > -30) {
    const w = r.range(6, 16);
    const len = r.chance(0.3) ? r.range(maxLen * 0.5, maxLen) : r.range(4, maxLen * 0.45);
    const tx = x - w * 0.5;
    const ty = top + len;
    d += `L${pt(x - w * 0.3, top + r.range(-3, 3))}L${pt(tx, ty)}L${pt(x - w * 0.7, top + r.range(-3, 3))}`;
    if (len > maxLen * 0.5) tips.push([tx, ty]);
    x -= w;
  }
  return { d: `${d}L-30 ${f1(top)}Z`, tips };
}

function paintCaveFront(p: Painter, r: Prng, st: Stage): void {
  const e = p.env;
  const pal = PAL.souterrain;
  const pts = st.water === "lac" ? hillPts(r, 189, 4, 2) : hillPts(r, 176, 8, 3);
  liftEdges(pts, st.water === "lac" ? 32 : 24);
  let spikes = "";
  for (const side of [-1, 1]) {
    const x = side < 0 ? r.range(22, 50) : r.range(350, 378);
    const h = r.range(30, 52);
    const w = r.range(12, 18);
    const y = yAt(pts, x) + 4;
    spikes += `M${pt(x - w / 2, y)}Q${pt(x - w * 0.2, y - h * 0.5)} ${pt(x, y - h)}Q${pt(x + w * 0.2, y - h * 0.5)} ${pt(x + w / 2, y)}Z`;
  }
  drawLayer(p, "sol", pts, 0.95, pal.near, { smooth: true, rim: false, texture: spikes });
  const b = new Batch();
  const mush = treeColors(p, 0.95);
  const spots: Pt[] = [];
  for (let i = 0; i < 4; i++) {
    const x = r.chance(0.5) ? r.range(60, 140) : r.range(260, 340);
    const y = yAt(pts, x) + 3;
    spots.push([x, y]);
    addTree(b, r, "champignon", x, y, r.range(7, 13), mush, e.lightFromLeft);
  }
  p.add(<g key={p.key()}>{spots.map(([x, y], i) => <circle key={i} cx={f1(x)} cy={f1(y - 8)} r="12" fill={p.glow(pal.accent, 0.3)} />)}</g>);
  p.batch(b);
  const cx = r.chance(0.5) ? r.range(20, 70) : r.range(330, 380);
  p.add(crystalCluster(p, r, cx, yAt(pts, cx) + 4, r.range(10, 15), r.pick(CRYSTALS), p.key()));
  const ceil = stalactites(r, 8, 44);
  p.add(<path key={p.key()} d={ceil.d} fill={p.tone(pal.mid, 0.85)} />);
  const drips: El[] = ceil.tips.slice(0, 5).map(([x, y], i) => {
    const dur = r.range(3, 6);
    return <circle key={i} class="scene-drip" cx={f1(x)} cy={f1(y + 1)} r="0.8" fill="#9fe8ff" opacity="0.6" style={anim(dur, -r.range(0, dur), { dy: px(160 - y) })} />;
  });
  p.add(<g key={p.key()}>{drips}</g>);
}

function paintForeground(p: Painter, st: Stage): void {
  const e = p.env;
  const pal = PAL[e.biome];
  const r = rngFor("avant", e.seed, e.biome, e.kind);
  if (e.under) {
    paintCaveFront(p, r, st);
    return;
  }
  const port = st.water === "mer" && (e.kind === "port" || e.kind === "lac");
  const pts = st.water === "lac" ? hillPts(r, 189, 4, 2) : port ? hillPts(r, 187, 4, 2) : hillPts(r, 175, 7, 2.6);
  liftEdges(pts, st.water === "lac" ? 32 : port ? 18 : 22);
  drawLayer(p, "avant", pts, 0.95, pal.near, { smooth: true, rim: false, albedo: pal.albedo });
  const grassC = p.tone(mix(pal.near, "#9bc46a", 0.18), 0.98, pal.albedo);
  switch (e.biome) {
    case "plaine":
      p.add(<path key={p.key()} d={grassD(r, pts, -20, 420, 2.2, 3, 7)} fill={grassC} />);
      if (!e.night) flowers(p, r, pts);
      break;
    case "foret":
    case "jungle": {
      const fern = new Batch();
      const c = p.tone(e.biome === "jungle" ? "#2c6e3a" : "#3a6a3a", 0.98);
      for (let i = 0; i < 4; i++) {
        const x = r.chance(0.5) ? r.range(10, 120) : r.range(280, 390);
        fern.fill(c, fanD(r, x, yAt(pts, x) + 3, r.range(10, 16), 7, 0.35, Math.PI - 0.35, 0.4));
      }
      p.batch(fern);
      p.add(<path key={p.key()} d={grassD(r, pts, -20, 420, 4, 2, 5)} fill={grassC} />);
      if (e.biome === "jungle") jungleFrame(p, r);
      break;
    }
    case "montagne":
    case "toundra": {
      let rocks = "";
      for (let i = 0; i < 4; i++) {
        const x = r.chance(0.5) ? r.range(10, 130) : r.range(270, 390);
        rocks += rockD(r, x, yAt(pts, x) + 4, r.range(10, 22), r.range(5, 11));
      }
      p.add(<path key={p.key()} d={rocks} fill={p.tone(pal.rock, 0.96)} />);
      const tuft = e.biome === "toundra" ? p.tone("#b9a77a", 0.96) : grassC;
      p.add(<path key={p.key()} d={grassD(r, pts, -20, 420, 7, 2, 5)} fill={tuft} />);
      break;
    }
    case "marais": {
      const water = p.def("mare", (id) => (
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color={mix(e.sky[2], "#2a3a2e", 0.45)} />
          <stop offset="1" stop-color={mix(e.sky[0], "#16201a", 0.6)} />
        </linearGradient>
      ));
      let pools = "";
      let shine = "";
      let pads = "";
      for (let i = 0; i < 3; i++) {
        const cx = r.range(40, 360);
        const cy = r.range(179, 190);
        const rx = r.range(28, 58);
        const ry = r.range(3.5, 6);
        pools += ellipseD(cx, cy, rx, ry);
        shine += `M${pt(cx - rx * 0.5, cy - ry * 0.2)}h${f1(rx * 0.4)}M${pt(cx + rx * 0.1, cy + ry * 0.3)}h${f1(rx * 0.3)}`;
        for (let k = 0; k < 3; k++) pads += ellipseD(cx + r.range(-0.6, 0.6) * rx, cy + r.range(-0.3, 0.3) * ry, r.range(1.6, 2.6), r.range(0.6, 1));
      }
      p.add(
        <g key={p.key()}>
          <path d={pools} fill={water} />
          <path d={shine} stroke={mix(e.sky[3], "#ffffff", 0.3)} stroke-width="0.6" opacity="0.45" />
          <path d={pads} fill={p.tone("#4f7a3a", 0.95)} />
        </g>,
      );
      for (let i = 0; i < 5; i++) {
        const x = r.range(10, 390);
        reedClump(p, r, x, yAt(pts, x) + 3, 0.96);
      }
      break;
    }
    case "desert": {
      let rip = "";
      for (let i = 0; i < 10; i++) {
        const x = r.range(-10, 400);
        const w = r.range(14, 34);
        rip += `M${pt(x, yAt(pts, x) + r.range(4, 22))}q${f1(w / 2)} -1.6 ${f1(w)} 0`;
      }
      p.add(<path key={p.key()} d={rip} stroke={p.tone(mix(pal.near, "#ffffff", 0.3), 0.95)} stroke-width="0.6" fill="none" opacity="0.55" />);
      const b = new Batch();
      const x = r.chance(0.5) ? r.range(24, 70) : r.range(330, 376);
      addTree(b, r, r.pick(["cactus", "palmier"] as const), x, yAt(pts, x) + 3, r.range(26, 40), treeColors(p, 0.97), e.lightFromLeft);
      p.batch(b);
      break;
    }
    case "volcan": {
      let rocks = "";
      for (let i = 0; i < 4; i++) {
        const x = r.chance(0.5) ? r.range(10, 130) : r.range(270, 390);
        rocks += rockD(r, x, yAt(pts, x) + 4, r.range(10, 20), r.range(5, 10));
      }
      p.add(<path key={p.key()} d={rocks} fill={p.tone("#151112", 1)} />);
      let cracks = "";
      for (let i = 0; i < 4; i++) {
        let x = r.range(10, 390);
        let y = yAt(pts, x) + r.range(6, 18);
        cracks += `M${pt(x, y)}`;
        for (let k = 0; k < 4; k++) {
          x += r.range(5, 12) * (r.chance(0.5) ? 1 : -1);
          y += r.range(-1, 4);
          cracks += `L${pt(x, y)}`;
        }
      }
      p.add(
        <g key={p.key()}>
          <path class="scene-glow-pulse" d={cracks} stroke="#ff5a1a" stroke-width="2.4" opacity="0.4" fill="none" style={anim(2.8, -1)} />
          <path d={cracks} stroke="#ffb347" stroke-width="0.7" fill="none" />
        </g>,
      );
      break;
    }
    case "cote": {
      if (port) {
        const foam = pts.map(([x, y]): Pt => [x, y + 0.6]);
        p.add(<path key={p.key()} class="scene-surf" d={smoothD(foam)} fill="none" stroke="#f4fbff" stroke-width="1.3" opacity="0.75" style={anim(3.2, 0)} />);
      } else p.add(<path key={p.key()} d={grassD(r, pts, -20, 420, 6, 3, 7)} fill={p.tone("#8a9a5a", 0.96)} />);
      break;
    }
  }
  if (st.water === "lac") {
    for (const x of [r.range(20, 70), r.range(330, 380)]) reedClump(p, r, x, yAt(pts, x) + 3, 0.96);
  }
}

// ───────── Particules et voiles ─────────

function risingEls(r: Prng, s: Spark, cls: string, prefix: string): El[] {
  const out: El[] = [];
  for (let i = 0; i < s.count; i++) {
    const dur = r.range(2.4, 4.8) * (s.rise > 30 ? 1.3 : 1);
    out.push(
      <circle key={`${prefix}${i}`} class={cls} cx={f1(s.x + r.range(-s.spread, s.spread))} cy={f1(s.y - r.range(0, s.rise * 0.3))} r={f1(r.range(0.5, 1.1))}
        fill={r.pick(s.colors)} opacity="0.75" style={anim(dur, -r.range(0, dur), { dx: px(r.range(-10, 10)), dy: px(-s.rise * r.range(0.7, 1.2)) })} />,
    );
  }
  return out;
}

const birdD = (s: number): string => `M${pt(-3.2 * s, 0)}Q${pt(-1.6 * s, -1.8 * s)} 0 0Q${pt(1.6 * s, -1.8 * s)} ${pt(3.2 * s, 0)}`;
const crowD = (s: number): string => `M${pt(-4 * s, 0)}Q${pt(-2 * s, -2.2 * s)} ${pt(0, 0.2 * s)}Q${pt(2 * s, -2.2 * s)} ${pt(4 * s, 0)}Q${pt(2 * s, -0.8 * s)} ${pt(0, s)}Q${pt(-2 * s, -0.8 * s)} ${pt(-4 * s, 0)}Z`;
const batD = (s: number): string => polyD(([[-4.5, -0.5], [-3, -1.5], [-2.2, -0.4], [-1, -1.2], [0, 0.6], [1, -1.2], [2.2, -0.4], [3, -1.5], [4.5, -0.5], [3, 1], [1.5, 0.6], [0, 1.6], [-1.5, 0.6], [-3, 1]] as Pt[]).map(([x, y]): Pt => [x * s, y * s]));

function paintFlyers(p: Painter, r: Prng, st: Stage): void {
  const e = p.env;
  if (e.under) return;
  let kind: "oiseau" | "corbeau" | "chauve-souris" | null = null;
  let n = 0;
  if (e.night) {
    if (st.bats || e.danger > 0) {
      kind = "chauve-souris";
      n = e.danger ? 4 : 3;
    }
  } else if (e.danger > 0 || e.kind === "tombeau") {
    kind = "corbeau";
    n = 3;
  } else if (!e.overcast && e.weather !== "brume" && ["plaine", "cote", "montagne", "foret", "desert", "toundra"].includes(e.biome) && r.chance(0.7)) {
    kind = "oiseau";
    n = r.int(2, 4);
  }
  if (!kind) return;
  const color = kind === "oiseau" ? p.tone("#2a2a33", 0.6) : "#0b0a10";
  const els: El[] = [];
  for (let i = 0; i < n; i++) {
    const x = r.range(40, 360);
    const y = r.range(30, 95);
    const s = kind === "oiseau" ? r.range(0.8, 1.3) : r.range(1, 1.5);
    const d = kind === "chauve-souris" ? batD(s) : kind === "corbeau" ? crowD(s) : birdD(s);
    const dur = kind === "chauve-souris" ? r.range(14, 22) : r.range(26, 44);
    const flap = kind === "chauve-souris" ? 0.22 : kind === "corbeau" ? 0.55 : 0.42;
    els.push(
      <g key={i} transform={`translate(${pt(x, y)})`}>
        <g class="scene-fly" style={anim(dur, -r.range(0, dur), { fx0: px(-x - 30), fx1: px(430 - x) })}>
          <path class="scene-flap" d={d} fill={kind === "oiseau" ? "none" : color} stroke={kind === "oiseau" ? color : "none"} stroke-width="0.8" stroke-linecap="round" style={anim(flap, -r.range(0, flap))} />
        </g>
      </g>,
    );
  }
  p.add(<g key={p.key()}>{els}</g>);
}

function paintParticles(p: Painter, st: Stage): void {
  const e = p.env;
  const r = rngFor("particules", e.seed, e.kind, e.weather, e.phase);
  const greenMist = e.biome === "marais";
  if (e.weather === "brume" || greenMist || st.mist) {
    const strong = e.weather === "brume";
    const color = greenMist ? mix("#a9d98f", e.fog, e.night ? 0.55 : 0.35) : mix("#ffffff", e.fog, e.night ? 0.6 : 0.3);
    const fill = p.glow(color, strong ? 0.55 : 0.32);
    const n = strong ? 5 : 3;
    const els: El[] = [];
    for (let i = 0; i < n; i++) {
      const dur = r.range(18, 34);
      els.push(
        <ellipse key={i} class="scene-drift" cx={f1(r.range(40, 360))} cy={f1(130 + (i * 50) / n + r.range(-4, 4))} rx={f1(r.range(120, 200))} ry={f1(r.range(9, 16))}
          fill={fill} style={anim(dur, -r.range(0, dur), { mx: px(r.range(18, 40)) })} />,
      );
    }
    p.add(<g key={p.key()}>{els}</g>);
  }
  const glowTime = e.night || (e.phase === "crepuscule" && e.hour >= 20.5);
  const place = ["foret", "plaine", "marais", "jungle"].includes(e.biome) || e.kind === "foret" || e.kind === "clairiere";
  if (glowTime && place && !e.overcast && !e.under) {
    const wisp = e.biome === "marais";
    const core = wisp ? "#c8fff2" : "#f6ffb8";
    const halo = p.glow(wisp ? "#7dffd9" : "#d9ff7a", 0.7);
    const n = wisp ? 7 : 12;
    const els: El[] = [];
    for (let i = 0; i < n; i++) {
      const x = r.range(20, 380);
      const y = r.range(118, 186);
      const dur = r.range(5, 11);
      els.push(
        <g key={i} class="scene-firefly" style={anim(dur, -r.range(0, dur), { dx: px(r.range(-14, 14)), dy: px(r.range(-10, 6)) })}>
          <circle cx={f1(x)} cy={f1(y)} r={wisp ? "6" : "4.5"} fill={halo} />
          <circle cx={f1(x)} cy={f1(y)} r={wisp ? "1.3" : "0.9"} fill={core} />
        </g>,
      );
    }
    p.add(<g key={p.key()}>{els}</g>);
  }
  const sparks: El[] = [];
  st.embers.forEach((s, i) => sparks.push(...risingEls(r, s, "scene-ember", `e${i}-`)));
  st.motes.forEach((s, i) => sparks.push(...risingEls(r, s, "scene-mote", `m${i}-`)));
  if (e.biome === "volcan") {
    for (let i = 0; i < 12; i++) {
      const dur = r.range(3.5, 7);
      sparks.push(
        <circle key={`v${i}`} class="scene-ember" cx={f1(r.range(10, 390))} cy={f1(r.range(150, 196))} r={f1(r.range(0.5, 1))} fill={r.pick(["#ffb347", "#ff6a1f", "#ffd27a"])}
          opacity="0.7" style={anim(dur, -r.range(0, dur), { dx: px(r.range(-14, 14)), dy: px(-r.range(40, 80)) })} />,
      );
    }
  }
  if (sparks.length) p.add(<g key={p.key()}>{sparks}</g>);
  if (e.weather === "neige" && !e.under) {
    const els: El[] = [];
    for (let i = 0; i < 46; i++) {
      const y = r.range(0, 200);
      const rad = r.range(0.6, 1.7);
      const dur = r.range(6, 12) * (2.3 - rad) * 0.7;
      els.push(
        <circle key={i} class="scene-flake" cx={f1(r.range(-10, 410))} cy={f1(y)} r={f1(rad)} fill="#ffffff" opacity={f2(r.range(0.55, 0.95))}
          style={anim(dur, -r.range(0, dur), { y0: px(-y - 6), dy: "216px", sw: px(r.range(4, 12)) })} />,
      );
    }
    p.add(<g key={p.key()}>{els}</g>);
  }
  if ((e.weather === "pluie" || e.weather === "orage") && !e.under) {
    const color = e.night ? "#9fb4d8" : "#dfe8f2";
    const n = e.weather === "orage" ? 60 : 44;
    const els: El[] = [];
    for (let i = 0; i < n; i++) {
      const x = r.range(-10, 440);
      const y = r.range(0, 200);
      const len = r.range(6, 11);
      const dur = r.range(0.55, 0.95);
      els.push(
        <path key={i} class="scene-drop" d={`M${pt(x, y)}l${f1(-len * 0.18)} ${f1(len)}`} stroke={color} stroke-width="0.7" stroke-linecap="round" opacity={f2(r.range(0.3, 0.6))}
          style={anim(dur, -r.range(0, dur), { y0: px(-y - 12), dx: "-40px" })} />,
      );
    }
    p.add(<g key={p.key()}>{els}</g>);
  }
  paintFlyers(p, r, st);
  if (e.weather === "orage" && !e.under) {
    const s = stormTiming(e);
    p.add(<g key={p.key()} class="scene-flash" style={anim(s.dur, s.delay)}><rect x="-20" y="-20" width="440" height="240" fill="#dfe7ff" opacity="0.2" /></g>);
  }
}

function paintOverlays(p: Painter): void {
  const e = p.env;
  if (e.danger > 0) {
    const tint = p.def("menace", (id) => (
      <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="#3a0618" stop-opacity={e.danger === 2 ? "0.5" : "0.32"} />
        <stop offset="0.7" stop-color="#1a0410" stop-opacity="0.12" />
        <stop offset="1" stop-color="#1a0410" stop-opacity="0.22" />
      </linearGradient>
    ));
    p.add(<rect key={p.key()} x="-20" y="-20" width="440" height="240" fill={tint} />);
  }
  const vign = p.def("vignette", (id) => (
    <radialGradient id={id} gradientUnits="userSpaceOnUse" cx="200" cy="96" r="250">
      <stop offset="0.55" stop-color="#03050c" stop-opacity="0" />
      <stop offset="1" stop-color="#03050c" stop-opacity={e.danger ? "0.7" : "0.45"} />
    </radialGradient>
  ));
  p.add(<rect key={p.key()} x="-20" y="-20" width="440" height="240" fill={vign} />);
  if (e.combat) {
    const red = p.def("combat", (id) => (
      <radialGradient id={id} gradientUnits="userSpaceOnUse" cx="200" cy="112" r="250">
        <stop offset="0.32" stop-color="#b3121f" stop-opacity="0" />
        <stop offset="0.72" stop-color="#b3121f" stop-opacity="0.45" />
        <stop offset="1" stop-color="#7a0610" stop-opacity="0.85" />
      </radialGradient>
    ));
    const r = rngFor("combat", e.seed);
    const els: El[] = [<rect key="v" class="scene-combat" x="-20" y="-20" width="440" height="240" fill={red} />];
    for (let i = 0; i < 14; i++) {
      const a = r.range(0, Math.PI * 2);
      const cx = 200 + r.range(-50, 50);
      const cy = 140 + r.range(-14, 10);
      const len = r.range(3, 6);
      const dist = r.range(14, 30);
      els.push(
        <path key={i} class="scene-spark" d={`M${pt(cx, cy)}l${f1(Math.cos(a) * len)} ${f1(-Math.sin(a) * len)}`} stroke={r.pick(["#ffe7a3", "#ffb347", "#ffffff"])}
          stroke-width="0.9" stroke-linecap="round" style={anim(r.range(1.2, 2.2), -r.range(0, 2), { dx: px(Math.cos(a) * dist), dy: px(-Math.sin(a) * dist) })} />,
      );
    }
    for (let i = 0; i < 2; i++) {
      const cx = 200 + r.range(-40, 40);
      const cy = 138 + r.range(-10, 6);
      els.push(
        <path key={`c${i}`} class="scene-clash" d={polyD([[cx, cy - 7], [cx + 1.4, cy - 1.4], [cx + 7, cy], [cx + 1.4, cy + 1.4], [cx, cy + 7], [cx - 1.4, cy + 1.4], [cx - 7, cy], [cx - 1.4, cy - 1.4]])}
          fill="#fff3d0" style={anim(r.range(1.6, 2.4), -r.range(0, 2))} />,
      );
    }
    p.add(<g key={p.key()}>{els}</g>);
  }
  const fade = p.def("fondu", (id) => (
    <linearGradient id={id} gradientUnits="userSpaceOnUse" x1="0" y1="138" x2="0" y2="200">
      <stop offset="0" class="scene-fade-0" />
      <stop offset="0.55" class="scene-fade-1" />
      <stop offset="1" class="scene-fade-2" />
    </linearGradient>
  ));
  p.add(<rect key={p.key()} x="-20" y="136" width="440" height="80" fill={fade} />);
}

// ───────── Composition ─────────

const SUBJECT_SCALE: Record<LocationKind, number> = {
  village: 1.3, hameau: 1.3, ville: 1.18, foret: 1, clairiere: 1, ruines: 1.22, donjon: 1.3, grotte: 1.12,
  lac: 1, col: 1, sanctuaire: 1.28, tour: 1, camp: 1.35, port: 1, tombeau: 1.25, chateau: 1.12,
};

function buildScene(o: BuildInput, uid: string): El {
  const p = new Painter(makeEnv(o), uid);
  paintSky(p);
  paintStars(p);
  paintAurora(p);
  paintSun(p);
  paintMoon(p);
  paintClouds(p);
  paintBolt(p);
  const st = paintTerrain(p);
  const first = p.out.length;
  const embers = st.embers.length;
  const motes = st.motes.length;
  paintSubject(p, st);
  const k = SUBJECT_SCALE[o.kind];
  if (k !== 1) {
    // le sujet est dessiné à l'échelle 1 puis agrandi autour de sa base (les particules suivent)
    const items = p.out.splice(first);
    p.add(<g key={p.key()} transform={`translate(${pt(st.sx, st.base)}) scale(${k}) translate(${pt(-st.sx, -st.base)})`}>{items}</g>);
    const follow = (s: Spark): void => {
      s.x = st.sx + (s.x - st.sx) * k;
      s.y = st.base + (s.y - st.base) * k;
      s.spread *= k;
      s.rise *= k;
    };
    st.embers.slice(embers).forEach(follow);
    st.motes.slice(motes).forEach(follow);
  }
  paintForeground(p, st);
  paintParticles(p, st);
  paintOverlays(p);
  return (
    <g>
      <defs>{p.defs}</defs>
      {p.out}
    </g>
  );
}

/** Même illustration avec une ambiance imposée (aperçus, galerie, tests visuels). */
export interface SceneArtProps {
  biome: Biome; kind: LocationKind; name: string; seed: string;
  phase: ScenePhase; weather: SceneWeather;
  /** heure décimale de Paris (position du soleil et de la lune) */
  hour: number;
  /** âge de la lune : 0 = nouvelle, 0,5 = pleine */
  moon: number;
  danger: number; inCombat?: boolean; height?: number;
}

export function SceneArt(props: SceneArtProps) {
  const { biome, kind, name, seed, phase, weather } = props;
  const inCombat = !!props.inCombat;
  const halfHour = Math.floor(props.hour * 2) / 2;
  const moonStep = Math.round(props.moon * 60) / 60;
  const dangerTier: 0 | 1 | 2 = props.danger >= 5 ? 2 : props.danger >= 4 ? 1 : 0;
  const content = useMemo(() => {
    const input: BuildInput = { biome, kind, seed, phase, weather, hour: halfHour, moon: moonStep, danger: dangerTier, combat: inCombat };
    const uid = `sc${hashString([biome, kind, seed, phase, weather, halfHour, moonStep, dangerTier, inCombat ? 1 : 0].join("|")).toString(36)}`;
    return buildScene(input, uid);
  }, [biome, kind, seed, phase, weather, inCombat, dangerTier, halfHour, moonStep]);
  return (
    <div class={`scene scene-${phase}${inCombat ? " scene-in-combat" : ""}${dangerTier ? " scene-danger" : ""}`}>
      <svg class="scene-svg" viewBox="0 0 400 200" preserveAspectRatio="xMidYMid slice" width="100%" height={props.height ?? 210}
        role="img" aria-label={sceneLabel(name, kind, biome, phase, weather, inCombat)}>
        {content}
      </svg>
    </div>
  );
}

export function Scene(props: SceneProps) {
  const amb = sceneAmbience(props.seed, props.biome, props.now);
  return (
    <SceneArt biome={props.biome} kind={props.kind} name={props.name} seed={props.seed} phase={amb.phase} weather={amb.weather}
      hour={amb.hour} moon={amb.moon} danger={props.danger} inCombat={props.inCombat} height={props.height} />
  );
}
